import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { Card, CardContent } from "../../Components/UI/card";
import { Textarea } from "../../Components/UI/textarea";
import { TaskCompleteDialog } from "../../Components/Common/TaskCompleteDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/UI/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../Components/UI/hover-card";
import { Popover, PopoverTrigger, PopoverContent } from "../../Components/UI/popover";
import { X, Pencil, ArrowLeft, Check, Ban, Plus, FileText, Bell, Mail, Hourglass, HourglassIcon, BookUserIcon, Contact2, User, User2, SquareArrowOutUpRight, Circle, ClipboardList, ClockFading, Trash2, CircleCheckBig, XCircle, Calendar, RotateCw, ChevronsUp, History, ListTodo, Users, UserRoundCheck, UserRoundX, HelpCircle, UsersRound, Copy } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { updateIndexedDB } from "../../Utils/IndexedDBUtils";
import { useDesktop } from "../../Components/Common/IsDesktop";
import { canEditTask, getCurrentUser, getRelativeDateText, getThreadNameFromTypeID, getMyAgenciesMappedIDs, getMyAgencyIDForUserID, getTaskCategoryIDForUser, getTaskPriorityForUser, combineDateTime, isIOSDevice, openInExternalBrowser, resolveTaskAgencyID, extractObserverUserIDs, calculateSuggestedReminder, getBeforeDueReminder, getTaskCompletionWhatsAppMessage, getTaskActionWhatsAppMessage, createWhatsAppLink, playTaskSuccessSound } from "../../Utils/Utilities";
import { SpeechControls } from "../../Components/Common/SpeechControls";
import { getTableDateOrWeekday } from "../../Utils/DateTime";
import { setGlobalLoading } from "../../Store/Actions";
import { completeTaskAPI, cancelTaskAPI, rejectTaskAPI, remindTaskAPI, statusUpdateAPI, reopenTaskAPI, /* markAsDoneAPI, */ manageTaskAPI } from './TasksAPI';
import apiService from "../../Services/APIService";
import { setCookie } from "../../Utils/CookieHandler";
import { AlertBox } from "../../Components/Common/AlertBox";
import RecordEntry from '../Records/RecordsEntry';
import TaskEntry from './TaskEntry';
import ThreadSahiIcon from '../../Components/Common/ThreadSahiIcon';
import { useTaskByID } from "../../Hooks/useTaskByID";
import ConnectionDetails from "../../Modules/Connections/ConnectionDetails";
import ConnectionDetailsMobile from "../../Modules/Connections/ConnectionDetailsMobile";
import useIsMobile from "../../Hooks/useIsMobile";
import { checkIfInqOnlyMode, getAgencyDetails, getThreadTypesArr, getUserConfig, getUsersArr, getAllUsersArr } from "../../Store/Reducers";
import ContactActions from "../../Components/Common/ContactActions";
import { getAuthData } from "../../Utils/AuthUtils";
import { BsBellFill, BsHourglassTop, BsWhatsapp } from "react-icons/bs";
import { MdPhone } from "react-icons/md";
import { handlePhoneClickAction, handleWhatsAppClickAction } from "../../Utils/ContactLogUtils";
import TemplateDialog from "../../Components/Common/TemplateDialog";
import ConditionalDiv from "../../Components/Common/ConditionalDiv";
import { Badge } from "../../Components/UI/badge";
import { cn } from "../../Lib/Cn";
import { priorities, getPriorityDetails } from '../../Utils/Constants';
import { format, addDays } from "date-fns";
import { DatePicker } from "../../Components/Common/DatePicker";
import { Calendar as CalendarComponent } from "../../Components/UI/calendar";
import { TimeInputWithDialog } from "../../Components/Common/TimePickerWatch";
import { Label } from "../../Components/UI/label";
import { Input } from "../../Components/UI/input";
import TaskCategoryManager from '../../Components/Common/TaskCategoryManager';
import { Drawer, DrawerContent } from "../../Components/UI/drawer";


const UserRoundArrowLeft = ({ size = 16, className }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="m19 16-3 3" />
		<path d="M2 21a8 8 0 0 1 12.664-6.5" />
		<path d="M22 19h-6l3 3" />
		<circle cx="10" cy="8" r="5" />
	</svg>
);

const tskDetail = {
	dialogContent: "sm:!max-w-[33vw] sm:!w-[50vw] sm:!min-h-[26vh] sm:!max-h-[72vh] flex flex-col gap-0 bg-[var(--background)] overflow-visible",
	header: "border-b pb-3",
	gridMain: "p-4 overflow-y-auto custom-scroll h-full",
	leftCol: "space-y-6  overflow-y-scroll [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
	cardBase: "bg-white sahi-radius",
	cardPadding: "px-3 pb-3",
	inlineBadge: "inline-flex items-center gap-2 bg-gray-100 px-3 py-2 sahi-radius text-sm",
	smallBtn: "text-sm px-3 py-1.5 h-auto flex-1",
	threadTag: "inline-flex items-center gap-2 bg-gray-100 sahi-radius text-sm px-2.5 py-1.5",
};

// Tracks the visual viewport height so mobile drawers can cap their height instead of opening full-screen.
const useVisualViewportHeight = () => {
	const [height, setHeight] = useState(() => window.visualViewport ? window.visualViewport.height : window.innerHeight);

	useEffect(() => {
		if (!window.visualViewport) return;
		const handleResize = () => setHeight(window.visualViewport.height);
		window.visualViewport.addEventListener('resize', handleResize);
		return () => window.visualViewport.removeEventListener('resize', handleResize);
	}, []);

	return height;
};



function TaskDescriptionView({ description, title }) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [copied, setCopied] = useState(false);
	const textRef = useRef(null);

	useEffect(() => {
		setIsExpanded(false);
	}, [description]);

	useLayoutEffect(() => {
		if (textRef.current) {
			setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight + 2);
		}
	}, [description, isExpanded]);

	if (!description || description === title) return null;

	const handleCopy = (e) => {
		e.stopPropagation();
		navigator.clipboard.writeText(description || "");
		setCopied(true);
		toast.success("Description copied to clipboard", { duration: 2000 });
		setTimeout(() => setCopied(false), 2000);
	};

	const renderCopyButton = () => (
		<button
			type="button"
			onClick={handleCopy}
			disabled={copied}
			className={cn(
				"inline-flex items-center justify-center p-0.5 text-zinc-400 align-text-bottom rounded transition-colors focus:outline-none",
				copied
					? "cursor-default"
					: "hover:text-zinc-700 hover:bg-zinc-100 cursor-pointer"
			)}
			title={copied ? "Copied" : "Copy description"}
		>
			{copied ? (
				<Check className="w-3.5 h-3.5" />
			) : (
				<Copy className="w-3.5 h-3.5" />
			)}
		</button>
	);

	const hasToggle = isOverflowing || isExpanded;

	return (
		<div className="w-full mt-1" onClick={(e) => e.stopPropagation()}>
			<div
				ref={textRef}
				onClick={(e) => {
					if (hasToggle) {
						e.stopPropagation();
						setIsExpanded(!isExpanded);
					}
				}}
				className={cn(
					"font-normal text-sm text-sub-text-color break-words w-full whitespace-pre-wrap select-text transition-all duration-200",
					hasToggle ? "cursor-pointer" : "cursor-text",
					!isExpanded && "line-clamp-3 overflow-hidden"
				)}
			>
				{description}
				{!hasToggle && <span className="ml-1.5">{renderCopyButton()}</span>}
			</div>
			{hasToggle && (
				<div className="flex items-center gap-1.5 mt-1">
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							setIsExpanded(!isExpanded);
						}}
						className="text-xs font-semibold text-sahi-primary-color hover:underline cursor-pointer focus:outline-none inline-block"
					>
						{isExpanded ? "See less" : "See more"}
					</button>
					{renderCopyButton()}
				</div>
			)}
		</div>
	);
}

const TaskDetails = ({ isOpen, onClose, taskID, openedFromConnectionDetails = false, forceTaskClosed = false, forceCompletedDT = null, fallbackTaskData = null, onTaskUpdate, mobileActiveTasks = [], onNavigateTask }) => {
	const touchStartX = useRef(0);
	const touchStartY = useRef(0);
	const touchEndX = useRef(0);
	const touchEndY = useRef(0);
	const [swipeOffset, setSwipeOffset] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [disableTransition, setDisableTransition] = useState(false);
	const [fadeClass, setFadeClass] = useState("");

	const currentIndex = mobileActiveTasks.findIndex(t => {
		if (typeof t === 'object' && t !== null) {
			const id = t.taskID || t.tskID || t.TaskID;
			return String(id) === String(taskID);
		}
		return String(t) === String(taskID);
	});
	const prevTaskObj = currentIndex > 0 ? mobileActiveTasks[currentIndex - 1] : null;
	const nextTaskObj = currentIndex !== -1 && currentIndex < mobileActiveTasks.length - 1 ? mobileActiveTasks[currentIndex + 1] : null;

	const prevTaskID = prevTaskObj ? (typeof prevTaskObj === 'object' ? (prevTaskObj.taskID || prevTaskObj.tskID || prevTaskObj.TaskID) : prevTaskObj) : null;
	const nextTaskID = nextTaskObj ? (typeof nextTaskObj === 'object' ? (nextTaskObj.taskID || nextTaskObj.tskID || nextTaskObj.TaskID) : nextTaskObj) : null;

	const { taskData: prevHookData } = useTaskByID(prevTaskID);
	const { taskData: nextHookData } = useTaskByID(nextTaskID);

	const prevAdjacentData = prevTaskID ? (prevTaskObj && typeof prevTaskObj === 'object' ? prevTaskObj : prevHookData) : null;
	const nextAdjacentData = nextTaskID ? (nextTaskObj && typeof nextTaskObj === 'object' ? nextTaskObj : nextHookData) : null;

	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
		touchStartY.current = e.touches[0].clientY;
		touchEndX.current = e.touches[0].clientX;
		touchEndY.current = e.touches[0].clientY;
		setIsDragging(true);
		setSwipeOffset(0);
	};

	const handleTouchMove = (e) => {
		if (!isDragging) return;
		touchEndX.current = e.touches[0].clientX;
		touchEndY.current = e.touches[0].clientY;
		const diffX = touchEndX.current - touchStartX.current;
		const diffY = touchEndY.current - touchStartY.current;

		if (Math.abs(diffX) > Math.abs(diffY) * 0.5) {
			if ((diffX > 0 && currentIndex === 0) || (diffX < 0 && currentIndex === mobileActiveTasks.length - 1)) {
				setSwipeOffset(diffX * 0.2);
			} else {
				setSwipeOffset(diffX);
			}
		}
	};

	const handleTouchEnd = () => {
		if (!isDragging) return;
		setIsDragging(false);
		const diffX = touchEndX.current - touchStartX.current;
		const diffY = touchEndY.current - touchStartY.current;
		const screenWidth = window.innerWidth;
		const threshold = screenWidth * 0.15; // 75% Threshold

		if (Math.abs(diffX) > threshold && Math.abs(diffY) < 150) {
			const direction = diffX > 0 ? 1 : -1;
			const canNavigate = (direction === 1 && currentIndex > 0) || (direction === -1 && currentIndex < mobileActiveTasks.length - 1);

			if (canNavigate) {
				// Slide out completely to the edge
				setSwipeOffset(direction * screenWidth);

				// Wait for the slide-out to finish (matching the 0.25s duration below)
				setTimeout(() => {
					setDisableTransition(true);
					const targetTask = direction === 1 ? prevTaskObj : nextTaskObj;
					const targetID = targetTask && typeof targetTask === 'object'
						? (targetTask.taskID || targetTask.tskID || targetTask.TaskID)
						: targetTask;
					onNavigateTask(targetID);
					// Reset offset so the new task appears centered
					setSwipeOffset(0);

					// Re-enable transitions after the reset is painted
					setTimeout(() => {
						setDisableTransition(false);
					}, 50);
				}, 260);
				return;
			}
		}
		// Snap back if threshold not met or navigation not possible
		setSwipeOffset(0);
	};

	const { taskData: hookTaskData, loading } = useTaskByID(taskID);
	const taskData = useMemo(() => {
		if (!hookTaskData && !fallbackTaskData) return null;
		const merged = { ...fallbackTaskData, ...hookTaskData };
		if (fallbackTaskData?.createdDT) merged.createdDT = fallbackTaskData.createdDT;
		if (fallbackTaskData?.statusDT) merged.statusDT = fallbackTaskData.statusDT;
		if (fallbackTaskData?.updatedDT) merged.updatedDT = fallbackTaskData.updatedDT;
		return merged;
	}, [hookTaskData, fallbackTaskData]);
	const isDesktop = useDesktop();
	const dispatch = useDispatch();
	const isMobile = useIsMobile();
	const threadTypes = useSelector(getThreadTypesArr) || [];
	const isInqOnly = useSelector(checkIfInqOnlyMode)
	const { data: storeTasks = [] } = useSelector((state) => state.taskData || {});
	const users = useSelector(getAllUsersArr) || [];
	const agencyData = useSelector(getAgencyDetails);
	const authData = getAuthData();
	const currentUserID = Number(authData?.UserID);
	const currentUser = getCurrentUser();
	const [remindLoading, setRemindLoading] = useState(false);
	const [isDescExpanded, setIsDescExpanded] = useState(false);
	const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
	const [dialogStates, setDialogStates] = useState({
		taskEntry: false,
		recordEntry: false,
		confirmComplete: false,
		confirmCancel: false,
		confirmReject: false,
		confirmReopen: false,
		// confirmMarkDone: false, // Mark as Done is disabled for now (being reworked into a Status update flow)
		connectionDetails: false,
	});
	const [taskEntryFocusTarget, setTaskEntryFocusTarget] = useState(null);
	const [shareActionByWhatsApp, setShareActionByWhatsApp] = useState(false);

	const pendingRepeatAfterRecordRef = useRef(false);
	const pendingStatusNoteRef = useRef("");
	const [taskEntryMode, setTaskEntryMode] = useState('edit'); // 'edit' | 'repeat'
	const [repeatPrefillData, setRepeatPrefillData] = useState(null);
	const [statusUpdateText, setStatusUpdateText] = useState('');
	const [completionDialogPrefill, setCompletionDialogPrefill] = useState('');
	const [selectedOptionID, setSelectedOptionID] = useState(0);
	const [forceShowNoteField, setForceShowNoteField] = useState(false);
	const [isEditingStatus, setIsEditingStatus] = useState(false);
	const [requestExtensionOpen, setRequestExtensionOpen] = useState(false);
	const [customExtensionDate, setCustomExtensionDate] = useState(null);
	const [selectedExtensionDate, setSelectedExtensionDate] = useState(null);
	const [extensionCalendarOpen, setExtensionCalendarOpen] = useState(false);
	const statusTextareaRef = useRef(null);
	const updateStatusBtnRef = useRef(null);
	const isUpdatingStatusRef = useRef(false);
	const speechControlsRef = useRef(null);

	useEffect(() => {
		if (!isOpen) {
			setIsDescExpanded(false);
		}
	}, [isOpen]);

	useEffect(() => {
		setIsDescExpanded(false);
	}, [taskID]);
	const [updatesModalOpen, setUpdatesModalOpen] = useState(false);
	const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
	const [categoryManagerInitialView, setCategoryManagerInitialView] = useState('list');
	const [myCategories, setMyCategories] = useState(() => {
		try { return JSON.parse(getUserConfig()?.TaskCtgs || '[]'); } catch { return []; }
	});

	useEffect(() => {
		if (!isOpen) return;
		try {
			const parsed = JSON.parse(getUserConfig()?.TaskCtgs || '[]');
			setMyCategories(parsed);
		} catch { /* keep previous value on parse failure */ }
	}, [isOpen]);

	const [reminderModalOpen, setReminderModalOpen] = useState(false);
	const [reminderDate, setReminderDate] = useState(null);
	const [reminderTime, setReminderTime] = useState("17:00");
	const [savingReminder, setSavingReminder] = useState(false);
	const reminderVvHeight = useVisualViewportHeight();

	const [dueModalOpen, setDueModalOpen] = useState(false);
	const [dueDraftDate, setDueDraftDate] = useState(null);
	const [dueDraftTime, setDueDraftTime] = useState("17:00");
	const [savingDue, setSavingDue] = useState(false);
	const dueVvHeight = useVisualViewportHeight();

	const uConfig = getUserConfig();
	let userEODTime = '17:00';
	let userPlanTime = '10:00';
	if (uConfig?.TaskConfig) {
		try {
			const parsed = JSON.parse(uConfig.TaskConfig);
			if (parsed && parsed[0]) {
				userEODTime = parsed[0].EOD || '17:00';
				userPlanTime = parsed[0].PLN || '10:00';
			}
		} catch (err) {
			console.error('Failed to parse TaskConfig:', err);
		}
	}

	const getRoundedReminder = (candidateDate, nowTime, dueDateTime) => {
		if (!candidateDate) return null;
		const ms = 1000 * 60;
		let roundedMs = Math.ceil(candidateDate.getTime() / (5 * ms)) * (5 * ms);
		let roundedDate = new Date(roundedMs);
		if (roundedDate > dueDateTime) {
			return dueDateTime;
		}
		return roundedDate;
	};

	const getBeforeDueReminder = (dueDT, nowTime = new Date()) => {
		if (!dueDT || isNaN(dueDT.getTime())) return null;

		// Strategy: Try 1 hour before, then 45 min before, then 30 min before, then 15 min before
		// Return the first one that is > nowTime and < dueDT (so for 3:30 PM, 1h before is 2:30 which has passed, 45m before is 2:45 which has passed, next is 30m before = 3:00 PM!)
		for (const offsetMin of [60, 45, 30, 15]) {
			const candidate = new Date(dueDT.getTime() - offsetMin * 60 * 1000);
			const cMin = candidate.getMinutes();
			candidate.setMinutes(cMin - (cMin % 15), 0, 0);
			if (candidate < dueDT && candidate > nowTime) {
				return candidate;
			}
		}

		return null;
	};

	const getSuggestedReminder = (dueDateTime, now = new Date()) => {
		return calculateSuggestedReminder(dueDateTime, userPlanTime, now);
	};

	const getSuggestedReminderTimeForDate = (newDate, currentReminderTime) => {
		const now = new Date();
		const dueDateTime = taskData?.dueDT ? new Date(taskData.dueDT) : null;

		const isToday = format(newDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');

		let checkTime = currentReminderTime || '17:00';
		if (isToday) {
			const [h, m] = checkTime.split(':').map(Number);
			const checkDT = new Date(now);
			checkDT.setHours(h, m, 0, 0);
			if (checkDT <= now) {
				const target = new Date(now.getTime() + 15 * 60 * 1000);
				const minutes = target.getMinutes();
				const remainder = minutes % 15;
				let roundedMinutes = remainder === 0 ? minutes : minutes + (15 - remainder);
				let roundedHours = target.getHours();
				if (roundedMinutes >= 60) {
					roundedHours += 1;
					roundedMinutes = 0;
				}
				target.setHours(roundedHours % 24, roundedMinutes, 0, 0);

				if (dueDateTime && !isNaN(dueDateTime.getTime())) {
					const dueMidnight = new Date(dueDateTime);
					dueMidnight.setHours(0, 0, 0, 0);
					const newDateMidnight = new Date(newDate);
					newDateMidnight.setHours(0, 0, 0, 0);
					if (dueMidnight.getTime() === newDateMidnight.getTime()) {
						if (target >= dueDateTime) {
							const reminderTime = getBeforeDueReminder(dueDateTime, now);
							if (reminderTime && reminderTime > now) {
								return format(reminderTime, 'HH:mm');
							}
							return format(dueDateTime, 'HH:mm');
						}
					}
				}
				return format(target, 'HH:mm');
			}
		}

		if (dueDateTime && !isNaN(dueDateTime.getTime())) {
			const dueMidnight = new Date(dueDateTime);
			dueMidnight.setHours(0, 0, 0, 0);
			const newDateMidnight = new Date(newDate);
			newDateMidnight.setHours(0, 0, 0, 0);

			if (newDateMidnight.getTime() === dueMidnight.getTime()) {
				const [h, m] = checkTime.split(':').map(Number);
				const checkDT = new Date(dueDateTime);
				checkDT.setHours(h, m, 0, 0);
				if (checkDT > dueDateTime) {
					const reminderTime = getBeforeDueReminder(dueDateTime, now);
					if (reminderTime && reminderTime > now) {
						return format(reminderTime, 'HH:mm');
					}
					return format(dueDateTime, 'HH:mm');
				}
			}
		}

		return checkTime;
	};


	const handleDueDraftDateChange = (date) => {
		if (!date) {
			setDueDraftDate(null);
			return;
		}
		const newDateStr = format(date, 'dd-MMM-yy');
		const now = new Date();

		// Determine final due time
		const selectedDate = new Date(date);
		selectedDate.setHours(0, 0, 0, 0);
		const todayMidnight = new Date(now);
		todayMidnight.setHours(0, 0, 0, 0);
		const isToday = selectedDate.getTime() === todayMidnight.getTime();

		let finalTime = dueDraftTime || '17:00';
		let timeModified = false;

		const parseCustomDate = (dObj) => {
			if (!dObj) return null;
			return new Date(dObj);
		};

		const prevDateObj = parseCustomDate(dueDraftDate);
		let isPreviousPast = false;
		if (prevDateObj && !isNaN(prevDateObj.getTime())) {
			prevDateObj.setHours(0, 0, 0, 0);
			isPreviousPast = prevDateObj < todayMidnight;
		}

		if (isToday && isPreviousPast) {
			const [eodH, eodM] = userEODTime.split(':').map(Number);
			const eodToday = new Date(now);
			eodToday.setHours(eodH, eodM, 0, 0);

			if (now < eodToday) {
				finalTime = userEODTime;
			} else {
				const bumped = new Date(now);
				const bMin = bumped.getMinutes();
				const bRem = bMin % 15;
				bumped.setMinutes(bRem === 0 ? bMin + 15 : bMin + (15 - bRem), 0, 0);
				finalTime = format(bumped, 'HH:mm');
			}
			timeModified = true;
		} else if (isToday && dueDraftTime) {
			const combined = new Date(combineDateTime(newDateStr, dueDraftTime));
			if (combined <= now) {
				const bumped = new Date(now);
				const bMin = bumped.getMinutes();
				const bRem = bMin % 15;
				bumped.setMinutes(bRem === 0 ? bMin + 15 : bMin + (15 - bRem), 0, 0);
				finalTime = format(bumped, 'HH:mm');
				timeModified = true;
			}
		}

		// Recalculate reminder only if user already has a reminder set
		const newDueDT = new Date(combineDateTime(newDateStr, finalTime));
		const suggested = getSuggestedReminder(newDueDT, now);

		setDueDraftDate(date);
		setDueDraftTime(finalTime);
		if (suggested && reminderDate) {
			setReminderDate(suggested.date);
			setReminderTime(suggested.time);
		}
	};

	const handleDueDraftTimeChange = (time) => {
		setDueDraftTime(time);
		if (dueDraftDate && time) {
			const dateStr = format(dueDraftDate, 'dd-MMM-yy');
			const combinedDT = new Date(combineDateTime(dateStr, time));
			if (!isNaN(combinedDT.getTime())) {
				const now = new Date();
				const suggested = getSuggestedReminder(combinedDT, now);
				if (suggested && reminderDate) {
					setReminderDate(suggested.date);
					setReminderTime(suggested.time);
				}
			}
		}
	};

	const getSuggestedTodayTime = (nowTime = new Date()) => {
		const candidate = new Date(nowTime);
		const curMin = candidate.getMinutes();
		const rem = curMin % 15;
		candidate.setMinutes(rem === 0 ? curMin + 15 : curMin + (15 - rem), 0, 0);
		if ((candidate.getTime() - nowTime.getTime()) < 15 * 60 * 1000) {
			candidate.setMinutes(candidate.getMinutes() + 15, 0, 0);
		}
		return format(candidate, 'HH:mm');
	};

	const handleSetDueToday = (e) => {
		e?.stopPropagation();
		e?.preventDefault();
		const now = new Date();
		const [eodH, eodM] = userEODTime.split(':').map(Number);
		const eodToday = new Date(now);
		eodToday.setHours(eodH, eodM, 0, 0);

		const todayStr = format(now, 'dd-MMM-yy');
		let finalTime = userEODTime;

		if (now >= eodToday) {
			finalTime = getSuggestedTodayTime(now);
		}

		const newDueDT = new Date(`${todayStr} ${finalTime}`);
		const suggested = getSuggestedReminder(newDueDT, now);

		setDueDraftDate(now);
		setDueDraftTime(finalTime);
		if (suggested && reminderDate) {
			setReminderDate(suggested.date);
			setReminderTime(suggested.time);
		}
	};

	const handleSetDueTmrw = (e) => {
		e?.stopPropagation();
		e?.preventDefault();
		const now = new Date();
		const tmrw = new Date(now);
		tmrw.setDate(tmrw.getDate() + 1);
		const tmrwStr = format(tmrw, 'dd-MMM-yy');
		const finalTime = userEODTime;

		const newDueDT = new Date(combineDateTime(tmrwStr, finalTime));
		const suggested = getSuggestedReminder(newDueDT, now);

		setDueDraftDate(tmrw);
		setDueDraftTime(finalTime);
		if (suggested && reminderDate) {
			setReminderDate(suggested.date);
			setReminderTime(suggested.time);
		}
	};

	const [dueModalType, setDueModalType] = useState('DUE'); // 'DUE' | 'RMDR' | 'BOTH'

	const openDueModal = (type = 'DUE') => {
		setDueModalType(type);
		let initialDueDT = null;
		if (taskData?.dueDT) {
			const d = new Date(taskData.dueDT);
			if (!isNaN(d.getTime())) {
				setDueDraftDate(d);
				setDueDraftTime(format(d, 'HH:mm'));
				initialDueDT = d;
			} else {
				const defDate = new Date();
				setDueDraftDate(defDate);
				setDueDraftTime("17:00");
				initialDueDT = new Date(combineDateTime(format(defDate, 'dd-MMM-yy'), "17:00"));
			}
		} else {
			const defDate = new Date();
			setDueDraftDate(defDate);
			setDueDraftTime("17:00");
			initialDueDT = new Date(combineDateTime(format(defDate, 'dd-MMM-yy'), "17:00"));
		}

		if (canEditDue) {
			if (taskData?.rmdrDT) {
				const rd = new Date(taskData.rmdrDT);
				const isReminderInPast = !isNaN(rd.getTime()) && rd < new Date();
				if (!isNaN(rd.getTime()) && !isReminderInPast) {
					setReminderDate(rd);
					setReminderTime(format(rd, 'HH:mm'));
				} else if (type === 'RMDR') {
					presetReminderToDueDT(initialDueDT);
				} else {
					setReminderDate(null);
					setReminderTime(userPlanTime);
				}
			} else if (type === 'RMDR') {
				presetReminderToDueDT(initialDueDT);
			} else {
				setReminderDate(null);
				setReminderTime(userPlanTime);
			}
		}

		setDueModalOpen(true);
	};

	const handleSaveDue = async () => {
		if (!dueDraftDate) {
			toast.error("Please select a date.");
			return;
		}
		const dateStr = format(dueDraftDate, 'dd-MMM-yy');
		const combinedStr = `${dateStr} ${dueDraftTime || '17:00'}`;
		const combinedDT = new Date(combinedStr);
		if (isNaN(combinedDT.getTime())) {
			toast.error("Invalid date or time.");
			return;
		}

		setSavingDue(true);
		// const taskAgencyID = Number(taskData?.AgencyID || 0);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);

		let rmdrDT = '';
		let isRmdrUpdate = 0;

		if (canEditDue) {
			if (reminderDate) {
				const rmdrError = getReminderDTError(reminderDate, reminderTime);
				if (rmdrError) {
					// Verify if it exceeds the new combined due DT
					const dateStrRmdr = format(reminderDate, 'dd-MMM-yy');
					const combinedStrRmdr = `${dateStrRmdr} ${reminderTime || '17:00'}`;
					const combinedDTRmdr = new Date(combinedStrRmdr);
					const isTaskOverdue = combinedDT < new Date();
					if (!isTaskOverdue && combinedDTRmdr > combinedDT) {
						toast.error("Reminder cannot be set after the task's due date/time.");
						setSavingDue(false);
						apiService.clearAgencyContext();
						return;
					}
					if (combinedDTRmdr < new Date()) {
						toast.error("Reminder time cannot be in the past.");
						setSavingDue(false);
						apiService.clearAgencyContext();
						return;
					}
				}
				const dateStrRmdr = format(reminderDate, 'dd-MMM-yy');
				const combinedStrRmdr = `${dateStrRmdr} ${reminderTime || '17:00'}`;
				const combinedDTRmdr = new Date(combinedStrRmdr);
				rmdrDT = format(combinedDTRmdr, 'dd-MMM-yy hh:mm aa');
				isRmdrUpdate = 1;
				setCookie('DefaultTaskRemind', 'true');
			} else if (taskData?.rmdrDT) {
				rmdrDT = '';
				isRmdrUpdate = 1;
				setCookie('DefaultTaskRemind', 'false');
			}
		} else {
			if (taskData?.rmdrDT) {
				const currentRemindDT = new Date(taskData.rmdrDT);
				if (!isNaN(currentRemindDT.getTime()) && currentRemindDT < combinedDT) {
					rmdrDT = taskData.rmdrDT;
				} else {
					rmdrDT = '';
					isRmdrUpdate = 1;
					toast.info("Reminder removed because it was after the new due time.");
				}
			}
		}

		try {
			const originalDue = taskData?.dueDT ? new Date(taskData.dueDT) : null;
			const isDueModified = !originalDue || isNaN(originalDue.getTime()) || originalDue.getTime() !== combinedDT.getTime();

			if (!canEdit && isAdmin && isAssigneeMine) {
				// Admin assignee updating due date / reminder calls RMDR changeType
				await manageTaskAPI({
					...formatTaskForAPI(taskData),
					dueDT: combineDateTime(dateStr, dueDraftTime || '17:00'),
					dueTime: dueDraftTime,
					rmdrDT: rmdrDT,
					scheduleDT: rmdrDT || '',
					isRmdrUpdate: isRmdrUpdate
				}, 'RMDR');
				const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
				const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
				toast.success(`${classLabel} updated.`);
			} else if (canEdit && !isDueModified) {
				// Call reminder API mapping directly (RMDR) if only reminder was changed
				await manageTaskAPI({
					...formatTaskForAPI(taskData),
					scheduleDT: rmdrDT,
					isRmdrUpdate: isRmdrUpdate
				}, 'RMDR');
				toast.success(rmdrDT ? "Reminder updated." : "Reminder removed.");
			} else {
				// Call complete EDIT API if due was changed
				await manageTaskAPI({
					...formatTaskForAPI(taskData),
					dueDT: combineDateTime(dateStr, dueDraftTime || '17:00'),
					dueTime: dueDraftTime,
					rmdrDT: rmdrDT,
					scheduleDT: rmdrDT,
					isRmdrUpdate: isRmdrUpdate
				}, 'EDIT');
				const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
				const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
				toast.success(`${classLabel} updated.`);
			}

			setDueModalOpen(false);
			if (onTaskUpdate) onTaskUpdate();
		} catch (err) {
			console.error("Error saving task details:", err);
			const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
			const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
			toast.error(err?.message || `Failed to update ${classLabel.toLowerCase()} details.`);
		} finally {
			apiService.clearAgencyContext();
			setSavingDue(false);
		}
	};

	const openReminderModal = () => {
		if (canEditDue) {
			openDueModal('RMDR');
			return;
		}

		if (taskData?.rmdrDT) {
			const d = new Date(taskData.rmdrDT);
			const isReminderInPast = !isNaN(d.getTime()) && d < new Date();
			if (!isNaN(d.getTime()) && !isReminderInPast) {
				setReminderDate(d);
				setReminderTime(format(d, 'HH:mm'));
			} else {
				presetReminderToDueDT();
			}
		} else {
			presetReminderToDueDT();
		}
		setReminderModalOpen(true);
	};

	const presetReminderToDueDT = (explicitDueDT = null) => {
		const now = new Date();
		const dueDateTime = explicitDueDT || (dueDraftDate && dueDraftTime
			? new Date(combineDateTime(format(dueDraftDate, 'dd-MMM-yy'), dueDraftTime))
			: (taskData?.dueDT ? new Date(taskData.dueDT) : null));

		if (!dueDateTime || isNaN(dueDateTime.getTime())) return;

		const suggested = getSuggestedReminder(dueDateTime, now);
		if (suggested) {
			setReminderDate(suggested.date);
			setReminderTime(suggested.time);
		}
	};

	const getReminderDTError = (date, time) => {
		if (!date) return null;
		const dateStr = format(date, 'dd-MMM-yy');
		const combinedStr = `${dateStr} ${time || '17:00'}`;
		const combinedDT = new Date(combinedStr);
		if (isNaN(combinedDT.getTime())) return null;
		if (combinedDT < new Date()) return "Reminder time cannot be in the past.";
		const isTaskOverdue = taskData?.dueDT && new Date(taskData.dueDT) < new Date();
		if (!isTaskOverdue && taskData?.dueDT && combinedDT > new Date(taskData.dueDT)) return "Reminder cannot be set after the task's due date/time.";
		return null;
	};

	const handleReminderDateChange = (newDate) => {
		if (!newDate) {
			setReminderDate(null);
			return;
		}
		const suggestedTime = getSuggestedReminderTimeForDate(newDate, reminderTime);
		setReminderDate(newDate);
		setReminderTime(suggestedTime);
	};

	const handleReminderTimeChange = (newTime) => {
		const err = getReminderDTError(reminderDate, newTime);
		if (err) { toast.error(err); return; }
		setReminderTime(newTime);
	};

	const handleSaveReminder = async (isClear = false) => {
		let formattedRmdrDT = '';
		if (!isClear) {
			if (!reminderDate) {
				toast.error("Please select a date for the reminder.");
				return;
			}
			const reminderError = getReminderDTError(reminderDate, reminderTime);
			if (reminderError) {
				toast.error(reminderError);
				return;
			}
			const dateStr = format(reminderDate, 'dd-MMM-yy');
			const combinedStr = `${dateStr} ${reminderTime || '17:00'}`;
			const combinedDT = new Date(combinedStr);
			formattedRmdrDT = format(combinedDT, 'dd-MMM-yy hh:mm aa');
		}

		setSavingReminder(true);
		// const taskAgencyID = Number(taskData?.AgencyID || 0);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		try {
			await manageTaskAPI({
				...formatTaskForAPI(taskData),
				scheduleDT: formattedRmdrDT,
				isRmdrUpdate: 1
			}, 'RMDR');
			toast.success(isClear ? "Reminder removed." : "Reminder updated.");
			setReminderModalOpen(false);
			if (onTaskUpdate) onTaskUpdate();
		} catch (err) {
			console.error("Error saving reminder:", err);
			toast.error(err?.message || "Failed to update reminder.");
		} finally {
			apiService.clearAgencyContext();
			setSavingReminder(false);
		}
	};

	const selectedCategoryID = getTaskCategoryIDForUser(taskData, currentUserID) || 0;
	const myUserIDsForTask = getMyAgenciesMappedIDs('UserID');
	const isCreatorMine = myUserIDsForTask.includes(Number(taskData?.createdUserID)) || Number(taskData?.createdUserID) === currentUserID;
	const isAssigneeMine = myUserIDsForTask.includes(Number(taskData?.pocUserID || taskData?.uID)) || Number(taskData?.pocUserID || taskData?.uID) === currentUserID;
	const isTaskClosed = forceTaskClosed !== false || taskData?.status === 2 || taskData?.status === 0 || taskData?.status === 4 || taskData?.status === 3;
	const isCompleted = taskData?.status === 2;
	const isCancelled = taskData?.status === 0;
	const isRejected = taskData?.status === 3;
	const isObserverMine = (() => {
		const obs = taskData?.observerUserIDs || taskData?.ObserverUIDs || taskData?.obsUIDs || taskData?.ObsUIDs;
		if (!obs) return false;
		const arr = Array.isArray(obs) ? obs : String(obs).split(',').map(id => Number(id.trim())).filter(Boolean);
		return arr.includes(currentUserID) || arr.some(id => myUserIDsForTask.includes(Number(id)));
	})();
	// User is considered admin of the task's created agency if their current active agency or any profile in MyAgencies matching the task's created agency is an Admin
	const taskCreatedAgencyID = Number(taskData?.agencyID || taskData?.AgencyID || 0) || (taskData?.createdUserID ? getMyAgencyIDForUserID(Number(taskData.createdUserID)) : 0);
	const isTaskCreatedAgencyAdmin = (() => {
		if (currentUser?.IsAdmin && (Number(currentUser?.AgencyID) === taskCreatedAgencyID || taskCreatedAgencyID === 0)) {
			return true;
		}
		try {
			const raw = localStorage.getItem('MyAgencies');
			if (!raw) return false;
			const list = JSON.parse(raw);
			if (!Array.isArray(list)) return false;
			return list.some(item => (Number(item?.AgencyID) === taskCreatedAgencyID || taskCreatedAgencyID === 0) && (item?.IsAdmin === true || item?.IsAdmin === 1 || item?.isAdmin === true || item?.isAdmin === 1));
		} catch (e) {
			return false;
		}
	})();
	const isAdmin = isTaskCreatedAgencyAdmin;
	const taskClassNumber = Number(taskData?.class || taskData?.Class || taskData?.taskClass || taskData?.TaskClass || 1);
	const isDecisionOrApproval = taskClassNumber === 3 || taskClassNumber === 4;
	const canEdit = canEditTask(taskData) && !isTaskClosed;
	const canEditDue = (canEdit || (isAdmin && isAssigneeMine)) && !isTaskClosed && !isDecisionOrApproval;
	const canEditMyCategory = (isAssigneeMine || isCreatorMine) && !isTaskClosed;
	const show4Buttons = isAssigneeMine && isCreatorMine && !isTaskClosed;
	const canUpdateStatus = (isAssigneeMine || isCreatorMine || isObserverMine) && !isTaskClosed;
	const hasValidConnection = taskData?.connID !== 0 && (taskData?.connectionData?.bizName || taskData?.connectionData?.contactName);

	const [editingPocTitle, setEditingPocTitle] = useState(false);
	const [pocTitleDraft, setPocTitleDraft] = useState('');
	const [savingPocTitle, setSavingPocTitle] = useState(false);
	const pocImportant = getTaskPriorityForUser(taskData, currentUserID) === 'H';
	const isImportantForMe = (isCreatorMine && isAssigneeMine)
		? (taskData?.priority === 'H')
		: (isAssigneeMine ? pocImportant : (isCreatorMine && taskData?.priority === 'H'));

	const handleTogglePocPriority = async () => {
		if (!taskData?.taskID || isTaskClosed) return;
		if (!isAssigneeMine && !isCreatorMine) return;

		let newPocPriority = taskData?.pocPriority || 'M';
		let newPriority = taskData?.priority || 'M';

		let operation = 'POCEDIT';
		if (isAssigneeMine && !isCreatorMine) {
			newPocPriority = pocImportant ? 'M' : 'H';
		} else if (isCreatorMine) {
			newPriority = (taskData?.priority === 'H') ? 'M' : 'H';
			if (isAssigneeMine) {
				newPocPriority = newPriority;
			}
			operation = 'EDIT';
		}

		// const taskAgencyID = Number(taskData?.AgencyID || 0);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		try {
			const payload = {
				...formatTaskForAPI(taskData),
				pocTask: taskData?.pocTask || '',
				pocPriority: newPocPriority,
				priority: newPriority
			};
			const result = await manageTaskAPI(payload, operation);
			if (result.error === false) {
				const isCurrentlyH = isAssigneeMine ? (newPocPriority === 'H') : (newPriority === 'H');
				toast.success(isCurrentlyH ? 'Marked as important.' : 'Removed from important.');
				onTaskUpdate && onTaskUpdate();
			}
		} catch (err) {
			toast.error(err.message || 'Failed to update.');
		} finally {
			apiService.clearAgencyContext();
		}
	};

	const handleSavePocTitle = async () => {
		if (!taskData?.taskID || !isAssigneeMine || isTaskClosed) return;
		setSavingPocTitle(true);
		// const taskAgencyID = Number(taskData?.AgencyID || 0);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		try {
			const result = await manageTaskAPI({
				...formatTaskForAPI(taskData),
				pocTask: pocTitleDraft.trim(),
				pocPriority: taskData?.pocPriority || 'M'
			}, 'POCEDIT');
			if (result.error === false) {
				toast.success('Title updated.');
				setEditingPocTitle(false);
				onTaskUpdate && onTaskUpdate();
			}
		} catch (err) {
			toast.error(err.message || 'Failed to update title.');
		} finally {
			apiService.clearAgencyContext();
			setSavingPocTitle(false);
		}
	};

	const focusStatusTextarea = () => {
		window.setTimeout(() => {
			if (statusTextareaRef.current) {
				statusTextareaRef.current.focus();
				const len = statusTextareaRef.current.value?.length || 0;
				statusTextareaRef.current.setSelectionRange(len, len);
			}
		}, 50);
	};

	const formatTaskDisplayDate = (date) => {
		if (!date) return "";
		const targetDate = new Date(date);
		const currentYear = new Date().getFullYear();
		const targetYear = targetDate.getFullYear();
		const day = targetDate.getDate();
		const month = targetDate.toLocaleDateString('en-US', { month: 'short' });
		if (currentYear === targetYear) {
			return `${day}-${month}`;
		}
		const yearShort = targetYear.toString().slice(-2);
		return `${day}-${month}-${yearShort}`;
	};

	const handleApplyExtensionDate = async (targetDate) => {
		if (!targetDate) return;
		const formattedDate = formatTaskDisplayDate(targetDate);
		const extensionMessage = `Requesting extension till ${formattedDate}.`;
		setRequestExtensionOpen(false);
		setCustomExtensionDate(null);
		setSelectedExtensionDate(null);
		setStatusUpdateText(extensionMessage);
		await handleStatusUpdate(extensionMessage);
	};

	useEffect(() => {
		if (isOpen && taskData) {
			const initialNote = taskData?.statusNote || '';
			setStatusUpdateText(initialNote);
			setIsEditingStatus(false);
		}
		if (!isOpen) {
			setStatusUpdateText('');
			setIsEditingStatus(false);
		}
	}, [isOpen, taskData?.taskID]);

	const createEmailLink = (email) => `mailto:${email.trim()}`;

	const openTaskEntryWithFocus = (focusTarget = null) => {
		if (!canEdit) return;
		setTaskEntryFocusTarget(focusTarget);
		setDialogStates(prev => ({ ...prev, taskEntry: true }));
	};

	const handleTaskEntryOpenChange = (open) => {
		setDialogStates(prev => ({ ...prev, taskEntry: open }));
		if (!open) {
			setTaskEntryFocusTarget(null);
			setTaskEntryMode('edit');
			setRepeatPrefillData(null);
		}
	};

	const handleContactClick = () => {
		if (openedFromConnectionDetails) {
			onClose();
			return;
		}
		setDialogStates(prev => ({ ...prev, connectionDetails: true }));
	};

	const isReminderSentToday = (lastRmdrDT) => {
		if (!lastRmdrDT) return false;
		const lastReminderDate = new Date(lastRmdrDT);
		const today = new Date();
		return (
			lastReminderDate.getFullYear() === today.getFullYear() &&
			lastReminderDate.getMonth() === today.getMonth() &&
			lastReminderDate.getDate() === today.getDate()
		);
	};

	const canRemind = (taskData?.pocUserID || taskData?.uID) && !isAssigneeMine && isCreatorMine && !isTaskClosed;
	const remindedToday = isReminderSentToday(taskData?.lastRmdrDT);
	const showRemindBtn = canRemind && !remindedToday;

	const handleRemindTask = async () => {
		const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
		const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
		if (isReminderSentToday(taskData?.lastRmdrDT)) {
			toast.error(`You can only send one reminder for this ${classLabel.toLowerCase()} per day`);
			return;
		}

		setRemindLoading(true);
		try {
			const result = await remindTaskAPI(formatTaskForAPI(taskData));
			if (result.error === false) {
				toast.success(`Reminder sent to ${taskData?.pocUserName}.`);
			} else {
				toast.error(result.message || 'Failed to send reminder.');
			}
		} catch (error) {
			toast.error(error.message || 'Failed to send reminder. Please try again.');
		} finally {
			setRemindLoading(false);
		}
	};

	const handleRemindWhatsApp = () => {
		const assigneeID = Number(taskData?.pocUserID || 0);
		const userObj = users.find(u => Number(u.userID) === assigneeID);
		const phone = userObj?.mobile || '';
		const cc = userObj?.cc || '91';
		const taskName = taskData?.title || '';
		const taskClass = Number(taskData?.taskClass || 1);
		const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
		const message = `${classLabel} Reminder - ${taskName}`;
		const wspLink = `${createWhatsAppLink(phone, cc)}?text=${encodeURIComponent(message)}`;
		window.open(wspLink, '_blank');
	};

	const formatTaskForAPI = (task) => {
		if (!task) return {};
		const dateObj = task.dueDT ? new Date(task.dueDT) : null;
		const resolvedTaskAgencyID = Number(task.AgencyID || 0) || Number(authData?.AgencyID || 0);
		return {
			taskID: task.taskID,
			connID: task.connID,
			taskName: task.title,
			description: task.description,
			dueDT: dateObj ? combineDateTime(format(dateObj, 'dd-MMM-yy'), format(dateObj, 'HH:mm')) : null,
			dueTime: dateObj ? dateObj.toTimeString().slice(0, 5) : null,
			pocUserID: task.pocUserID,
			createdUserID: task.createdUserID,
			priority: task.priority,
			threads: task.threadIDs ? task.threadIDs.split(",") : '',
			status: task.status,
			taskType: task.type,
			connectionLocked: true,
			bizName: task?.connectionData?.bizName || '',
			contactName: task?.connectionData?.contactName || '',
			secConnIDs: task?.scIDs?.split(",").map(id => Number(id)).filter(Boolean),
			rmdrDT: task?.rmdrDT,
			myCategory: getTaskCategoryIDForUser(task, currentUserID) || 0,
			AgencyID: resolvedTaskAgencyID,
			taskAgencyID: resolvedTaskAgencyID,
			canComplete: task.canComplete,
			statusNote: task.statusNote,
			repeatAfter: task.repeatAfter || 0,
			repeatDT: task.repeatDT || '',
			taskClass: task.taskClass || 1,
			options: task.options || [],
			observerUserIDs: extractObserverUserIDs(task).join(","),
		};
	};



	const handleCompleteTask = async (addRecord = false, onlyClose = false, repeatTask = false, statusNote = "") => {
		if (onlyClose) {
			onClose();
			return;
		}
		setDialogStates(prev => ({ ...prev, confirmComplete: false }));
		if (addRecord) {
			setDialogStates(prev => ({ ...prev, recordEntry: true }));
			return;
		}

		if (shareActionByWhatsApp) {
			const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
			const currentUserID = getAuthData()?.UserID ? Number(getAuthData().UserID) : 0;
			const assigneeID = Number(taskData?.pocUserID || taskData?.POCUserID || taskData?.uID || 0);
			const creatorID = Number(taskData?.createdUserID || taskData?.CreatedUserID || 0);
			const isAssigneeMe = (assigneeID === currentUserID) || myUserIDs.includes(assigneeID);
			const notifyUserID = isAssigneeMe ? creatorID : assigneeID;

			if (notifyUserID) {
				const userObj = users.find(u => Number(u.userID || u.UserID || 0) === Number(notifyUserID));
				if (userObj && (userObj.mobile || userObj.Mobile)) {
					const phone = userObj.mobile || userObj.Mobile || '';
					const cc = userObj.cc || userObj.CC || '91';
					const message = getTaskCompletionWhatsAppMessage(taskData, statusNote);

					const cleanedPhone = String(phone).replace(/[^\d]/g, "");
					const cleanedCC = String(cc).replace(/[^\d]/g, "");
					const whatsappUrl = `https://api.whatsapp.com/send/?phone=${cleanedCC}${cleanedPhone}&text=${encodeURIComponent(message)}`;

					window.open(whatsappUrl, '_blank');
				}
			}
		}

		dispatch(setGlobalLoading(true));
		// const taskAgencyID = Number(taskData?.AgencyID || 0);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		try {
			const result = await completeTaskAPI({ ...formatTaskForAPI(taskData), repeatTask, statusNote, selectedOptionID });
			const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			if (result.error === false) {
				// playTaskSuccessSound();
				toast.success(`${classLabel} completed.`);
				onTaskUpdate && onTaskUpdate();
				onClose();
			} else {
				toast.error(result.message || `Failed to complete ${classLabel.toLowerCase()}.`);
			}
		} catch (error) {
			const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			toast.error(error.message || `Failed to complete ${classLabel.toLowerCase()}.`);
		} finally {
			apiService.clearAgencyContext();
			dispatch(setGlobalLoading(false));
			setShareActionByWhatsApp(false);
			setSelectedOptionID(0);
			onClose();
		}
	};

	// Submits the Status text box's current value via the STATUS action (not the set/tsk_Set API).
	const handleStatusUpdate = async (overrideText = null) => {
		if (isUpdatingStatusRef.current) return;
		isUpdatingStatusRef.current = true;

		dispatch(setGlobalLoading(true));
		// const taskAgencyID = resolveTaskAgencyID(taskData, Number(taskData?.AgencyID || 0));
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		const textToSave = overrideText !== null ? overrideText : statusUpdateText;
		try {
			const result = await statusUpdateAPI({ ...formatTaskForAPI(taskData), statusNote: textToSave });
			if (result.error === false) {
				toast.success('Status updated.');
				setIsEditingStatus(false);
			} else {
				toast.error(result.message || 'Failed to update status.');
				setIsEditingStatus(false);
				setStatusUpdateText(taskData?.statusNote || '');
			}
		} catch (error) {
			toast.error(error.message || 'Failed to update status. Please try again.');
			setIsEditingStatus(false);
			setStatusUpdateText(taskData?.statusNote || '');
		} finally {
			apiService.clearAgencyContext();
			dispatch(setGlobalLoading(false));
			isUpdatingStatusRef.current = false;
		}
	};

	const handleCancelTask = async (statusNote = "") => {
		setDialogStates(prev => ({ ...prev, confirmCancel: false }));

		if (shareActionByWhatsApp) {
			const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
			const currentUserID = getAuthData()?.UserID ? Number(getAuthData().UserID) : 0;
			const assigneeID = Number(taskData?.pocUserID || taskData?.POCUserID || taskData?.uID || 0);
			const creatorID = Number(taskData?.createdUserID || taskData?.CreatedUserID || 0);
			const isAssigneeMe = (assigneeID === currentUserID) || myUserIDs.includes(assigneeID);
			const notifyUserID = isAssigneeMe ? creatorID : assigneeID;

			if (notifyUserID) {
				const userObj = users.find(u => Number(u.userID || u.UserID || 0) === Number(notifyUserID));
				if (userObj && (userObj.mobile || userObj.Mobile)) {
					const phone = userObj.mobile || userObj.Mobile || '';
					const cc = userObj.cc || userObj.CC || '91';
					const message = getTaskActionWhatsAppMessage({
						action: 'CANCEL',
						task: taskData,
						statusNote,
						isAssigneeMe
					});

					const cleanedPhone = String(phone).replace(/[^\d]/g, "");
					const cleanedCC = String(cc).replace(/[^\d]/g, "");
					const whatsappUrl = `https://api.whatsapp.com/send/?phone=${cleanedCC}${cleanedPhone}&text=${encodeURIComponent(message)}`;

					window.open(whatsappUrl, '_blank');
				}
			}
		}

		dispatch(setGlobalLoading(true));

		let resolvedAgencyID = Number(taskData?.AgencyID || 0);
		if (isObserverMine && !isAssigneeMine && !isCreatorMine) {
			try {
				const myAgencies = JSON.parse(localStorage.getItem('MyAgencies') || '[]');
				const obs = taskData?.observerUserIDs || taskData?.ObserverUIDs || taskData?.obsUIDs || taskData?.ObsUIDs;
				const obsArr = Array.isArray(obs)
					? obs.map(Number)
					: String(obs || '').split(',').map(id => Number(id.trim())).filter(Boolean);
				const matchedAgency = myAgencies.find(agn => obsArr.includes(Number(agn.UserID)));
				if (matchedAgency) {
					resolvedAgencyID = Number(matchedAgency.AgencyID);
				} else {
					console.warn('[TaskCancel/Reject] No MyAgencies match found for observer UIDs:', obsArr, 'MyAgencies:', myAgencies.map(a => ({ UserID: a.UserID, AgencyID: a.AgencyID })));
				}
			} catch (e) {
				console.error('[TaskCancel/Reject] Error resolving observer agency:', e);
			}
		}

		// if (resolvedAgencyID) apiService.setAgencyContext(resolvedAgencyID);

		const isReject = (isObserverMine || !isAssigneeMine) && !isCreatorMine;
		const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
		const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";
		try {
			const result = isReject
				? await rejectTaskAPI({ ...formatTaskForAPI(taskData), statusNote })
				: await cancelTaskAPI({ ...formatTaskForAPI(taskData), statusNote });
			const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			if (result.error === false) {
				toast.success(isReject ? `${classLabel} rejected.` : `${classLabel} cancelled.`);
				onTaskUpdate && onTaskUpdate();
				onClose();
			} else {
				toast.error(result.message || `Failed to ${isReject ? 'reject' : 'cancel'} ${classLabel.toLowerCase()}.`);
			}
		} catch (error) {
			const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			toast.error(error.message || `Failed to ${isReject ? 'reject' : 'cancel'} ${classLabel.toLowerCase()}. Please try again.`);
		} finally {
			apiService.clearAgencyContext();
			dispatch(setGlobalLoading(false));
			setShareActionByWhatsApp(false);
		}
	};

	const handleRejectTask = async (statusNote = "") => {
		setDialogStates(prev => ({ ...prev, confirmReject: false }));

		if (shareActionByWhatsApp) {
			const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
			const currentUserID = getAuthData()?.UserID ? Number(getAuthData().UserID) : 0;
			const assigneeID = Number(taskData?.pocUserID || taskData?.POCUserID || taskData?.uID || 0);
			const creatorID = Number(taskData?.createdUserID || taskData?.CreatedUserID || 0);
			const isAssigneeMe = (assigneeID === currentUserID) || myUserIDs.includes(assigneeID);
			const notifyUserID = isAssigneeMe ? creatorID : assigneeID;

			if (notifyUserID) {
				const userObj = users.find(u => Number(u.userID || u.UserID || 0) === Number(notifyUserID));
				if (userObj && (userObj.mobile || userObj.Mobile)) {
					const phone = userObj.mobile || userObj.Mobile || '';
					const cc = userObj.cc || userObj.CC || '91';
					const message = getTaskActionWhatsAppMessage({
						action: 'REJECT',
						task: taskData,
						statusNote,
						isAssigneeMe
					});

					const cleanedPhone = String(phone).replace(/[^\d]/g, "");
					const cleanedCC = String(cc).replace(/[^\d]/g, "");
					const whatsappUrl = `https://api.whatsapp.com/send/?phone=${cleanedCC}${cleanedPhone}&text=${encodeURIComponent(message)}`;

					window.open(whatsappUrl, '_blank');
				}
			}
		}

		dispatch(setGlobalLoading(true));

		// Resolve the correct agency context for the rejecting user (assignee):
		// Use the task's AgencyID directly since the assignee belongs to that agency.
		const taskAgencyID = Number(taskData?.AgencyID || 0);
		// console.log('[TaskReject] Setting agency context from taskAgencyID:', taskAgencyID);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		// console.log('[TaskReject] Agency context set:', apiService.agencyContext);

		const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
		const classLabel = taskClass === 2 ? "Meeting" : taskClass === 3 ? "Decision" : taskClass === 4 ? "Approval" : "Task";

		try {
			const result = await rejectTaskAPI({ ...formatTaskForAPI(taskData), statusNote });
			const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			if (result.error === false) {
				toast.success(`${classLabel} rejected.`);
				onTaskUpdate && onTaskUpdate();
				onClose();
			} else {
				toast.error(result.message || `Failed to reject ${classLabel.toLowerCase()}.`);
			}
		} catch (error) {
			const taskClass = Number(taskData?.taskClass || taskData?.Class || taskData?.class || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			toast.error(error.message || `Failed to reject ${classLabel.toLowerCase()}. Please try again.`);
		} finally {
			apiService.clearAgencyContext();
			dispatch(setGlobalLoading(false));
			setShareActionByWhatsApp(false);
		}
	};

	const UpdatesTimelineContent = () => {
		const groupedUpdates = useMemo(() => {
			if (!taskData?.updates) return [];

			// Sort from latest to oldest
			const sorted = [...taskData.updates].sort((a, b) => {
				const aId = Number(a.UID || a.uid || 0);
				const bId = Number(b.UID || b.uid || 0);
				if (aId > 0 && bId > 0) {
					return bId - aId;
				}
				return new Date(b.UDT) - new Date(a.UDT);
			});

			const groups = [];
			for (const upd of sorted) {
				const lastGroup = groups[groups.length - 1];
				// Group consecutive updates by the same user (UUID)
				if (lastGroup && String(lastGroup.UUID) === String(upd.UUID)) {
					lastGroup.items.push({
						UTxt: upd.UTxt,
						UDT: upd.UDT
					});
				} else {
					groups.push({
						UUID: upd.UUID,
						mainUDT: upd.UDT,
						items: [{
							UTxt: upd.UTxt,
							UDT: upd.UDT
						}]
					});
				}
			}
			return groups;
		}, [taskData?.updates]);

		if (groupedUpdates.length === 0) {
			return (
				<div className="p-8 text-center text-sm text-sub-text-color">
					<History className="h-8 w-8 mx-auto mb-2 text-zinc-300 animate-pulse" />
					<p className="font-medium text-zinc-400">No updates yet</p>
				</div>
			);
		}

		return (
			<div className="relative">
				{/* Vertical Line starting at first circle and ending at last circle */}
				<div className="absolute top-[16px] bottom-[16px] w-0.5 bg-zinc-200 left-[7px]"></div>

				<div className="space-y-2">
					{groupedUpdates.map((group, idx) => {
						const isCreator = Number(group.UUID) === Number(taskData.createdUserID);
						const isMe = myUserIDsForTask.includes(Number(group.UUID)) || Number(group.UUID) === currentUserID;

						// Find updater's name
						let updaterName = "System";
						if (isMe) {
							updaterName = "You";
						} else {
							const matchedUser = users.find(u => Number(u.UserID) === Number(group.UUID));
							if (matchedUser) {
								updaterName = matchedUser.Name;
							} else if (isCreator) {
								updaterName = taskData.createdUserName || "Creator";
							} else {
								updaterName = taskData.pocUserName || "Assignee";
							}
						}

						return (
							<div key={idx} className="relative flex flex-col items-start">
								{/* Timeline Node Icon */}
								<div className={`absolute z-10 top-[10px] w-3 h-3 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${isMe ? 'bg-[var(--sahi-primary-color)]' : 'bg-[#bf5700]'
									} left-[2px]`}>
								</div>

								{/* Card Content */}
								<div className="w-full pl-6">
									<div className="inline-block bg-zinc-50 border border-zinc-200 rounded-lg p-[10px] text-left w-full transition-shadow">
										<div className="flex items-center justify-between w-full gap-2 mb-0.5">
											<span className="text-[11px] text-sub-text-color font-normal truncate">
												{updaterName}
											</span>
											<span className="text-[11px] text-sub-text-color font-normal flex-shrink-0">
												{getRelativeDateText(group.mainUDT)}
											</span>
										</div>
										<div className="space-y-2">
											{group.items.map((item, itemIdx) => (
												<div key={itemIdx} className={itemIdx > 0 ? "border-t border-zinc-200/60 pt-2" : ""}>
													{itemIdx > 0 && (
														<div className="flex items-center justify-between w-full gap-2 mb-0.5">
															<span className="text-[11px] text-sub-text-color font-normal truncate">
																{updaterName}
															</span>
															<span className="text-[11px] text-sub-text-color font-normal flex-shrink-0">
																{getRelativeDateText(item.UDT)}
															</span>
														</div>
													)}
													<p className="text-sm font-medium text-zinc-850 break-words whitespace-pre-wrap">
														{item.UTxt}
													</p>
												</div>
											))}
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const handleReopenTask = async () => {
		setDialogStates(prev => ({ ...prev, confirmReopen: false }));

		if (shareActionByWhatsApp) {
			const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
			const currentUserID = getAuthData()?.UserID ? Number(getAuthData().UserID) : 0;
			const assigneeID = Number(taskData?.pocUserID || taskData?.POCUserID || taskData?.uID || 0);
			const creatorID = Number(taskData?.createdUserID || taskData?.CreatedUserID || 0);
			const isAssigneeMe = (assigneeID === currentUserID) || myUserIDs.includes(assigneeID);
			const notifyUserID = isAssigneeMe ? creatorID : assigneeID;

			if (notifyUserID) {
				const userObj = users.find(u => Number(u.userID || u.UserID || 0) === Number(notifyUserID));
				if (userObj && (userObj.mobile || userObj.Mobile)) {
					const phone = userObj.mobile || userObj.Mobile || '';
					const cc = userObj.cc || userObj.CC || '91';
					const message = getTaskActionWhatsAppMessage({
						action: 'REOPEN',
						task: taskData,
						isAssigneeMe
					});

					const cleanedPhone = String(phone).replace(/[^\d]/g, "");
					const cleanedCC = String(cc).replace(/[^\d]/g, "");
					const whatsappUrl = `https://api.whatsapp.com/send/?phone=${cleanedCC}${cleanedPhone}&text=${encodeURIComponent(message)}`;

					window.open(whatsappUrl, '_blank');
				}
			}
		}

		dispatch(setGlobalLoading(true));
		// const taskAgencyID = Number(taskData?.AgencyID || 0);
		// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
		try {
			const result = await reopenTaskAPI(formatTaskForAPI(taskData));
			const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			if (result.error === false) {
				toast.success(`${classLabel} reopened.`);
				if (onTaskUpdate) {
					onTaskUpdate('reopen');
					onClose();
				} else {
					setTaskEntryMode('edit');
					setTaskEntryFocusTarget(null);
					setDialogStates(prev => ({ ...prev, taskEntry: true }));
				}
			} else {
				toast.error(result.message || `Failed to reopen ${classLabel.toLowerCase()}.`);
			}
		} catch (error) {
			const taskClass = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1);
			const classLabel = taskClass === 2 ? "Meeting Task" : taskClass === 3 ? "Decision Task" : taskClass === 4 ? "Approval Task" : "Task";
			toast.error(error.message || `Failed to reopen ${classLabel.toLowerCase()}. Please try again.`);
		} finally {
			apiService.clearAgencyContext();
			dispatch(setGlobalLoading(false));
			setShareActionByWhatsApp(false);
		}
	};

	if ((loading && !fallbackTaskData) || !taskData) {
		return null;
	}

	// Alias for outer taskData — used by TaskInformationSection's override param fallback
	// eslint-disable-next-line no-unused-vars
	const _primaryTaskData = taskData;

	const TaskInformationSection = (overrideTaskData = null) => {
		// When overrideTaskData is provided (adjacent panel), shadow outer taskData.
		// All existing internal references to taskData automatically use this local one.
		// eslint-disable-next-line no-shadow
		const taskData = overrideTaskData ?? _primaryTaskData;
		const isOverdue = taskData?.dueDT && new Date(taskData.dueDT) < new Date();
		const showReminder = taskData?.rmdrDT && !isOverdue;

		// Helper function to determine if date is "Today", "Yesterday", "Tomorrow"
		const getRelativeDay = (date) => {
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			const targetDate = new Date(date);
			targetDate.setHours(0, 0, 0, 0);

			const diffTime = targetDate - today;
			const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

			if (diffDays === 0) return 'Today';
			if (diffDays === -1) return 'Yesterday';
			if (diffDays === 1) return 'Tomorrow';
			return null;
		};

		const formatTime = (date) => {
			const d = new Date(date);
			if (d.getHours() === 23 && d.getMinutes() === 59) {
				return "by EOD";
			}
			return d.toLocaleTimeString('en-US', {
				hour: 'numeric',
				minute: '2-digit',
				hour12: true
			});
		};
		// Helper function to format date (Jan 28, 2026)
		const formatDate = (date) => {
			const targetDate = new Date(date);
			const currentYear = new Date().getFullYear();
			const targetYear = targetDate.getFullYear();

			const day = targetDate.getDate();
			const month = targetDate.toLocaleDateString('en-US', { month: 'short' });

			if (currentYear === targetYear) {
				return `${day}-${month}`;
			} else {
				const yearShort = targetYear.toString().slice(-2);
				return `${day}-${month}-${yearShort}`;
			}
		};

		// Component for displaying date/time with conditional formatting
		const DateTimeDisplay = ({ date, label, color, isRemind, endAdornment }) => {
			const relativeDay = getRelativeDay(date);
			const time = formatTime(date);
			const fullDate = formatDate(date);

			if (!isRemind) {
				// Remind format: day/date and time in single line
				return (
					<div className="flex items-center gap-2">
						<span className={` font-semibold ${color || ''}`}>
							{relativeDay || fullDate} ({time})
						</span>
						{endAdornment}
					</div>
				);
			} else if (relativeDay) {
				// Format 1: Today/Yesterday/Tomorrow - Small label, Big time
				return (
					<div className="flex flex-col">
						<span className={`text-sm ${color || ''}`}>{relativeDay}</span>
						<span className={`font-semibold ${color || ''} flex items-center gap-1`}>{time}{endAdornment}</span>
					</div>
				);
			} else {
				// Format 2: Future date - Small time, Big date
				return (
					<div className="flex flex-col gap-0">
						<span className={`text-sm ${color || ''}`}>{time}</span>
						<span className={`font-semibold ${color || ''} flex items-center gap-1`}>{fullDate}{endAdornment}</span>
					</div>
				);
			}
		};

		return (
			<div className="space-y-3">
				{/* Contact Card - FIRST */}
				{(taskData?.connectionData?.bizName || taskData?.connectionData?.contactName) && (
					<div className="bg-white rounded-lg cursor-pointer" onClick={handleContactClick}>
						<div className={isMobile ? 'p-3 pb-2' : 'p-3 pb-2'}>
							<div className="flex items-center text-sub-text-color gap-2">
								<Contact2 className="h-4 w-4" />
								<span className="text-sm font-medium cursor-pointer">Contact</span>
							</div>
							<div className="mt-2 flex items-center gap-1">
								<span className=" font-semibold">
									{taskData?.connectionData?.bizName || taskData?.connectionData?.contactName}
								</span>
								{taskData?.connID !== 0 && (
									<button
										onClick={(e) => { e.stopPropagation(); handleContactClick(); }}
										className="p-1 rounded-md hover:bg-gray-100 transition-colors"
									>
										<SquareArrowOutUpRight className="h-4 w-4 text-text-color" />
									</button>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Title and Description */}
				<div
					className={`w-full bg-white rounded-lg p-3 text-left ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
					onClick={() => { if (canEdit) openTaskEntryWithFocus("taskName"); }}
				>
					<div className="flex flex-col items-start justify-between w-full gap-2">
						<div className="flex items-start gap-2 w-full">
							{isAssigneeMine && !isCreatorMine && editingPocTitle ? (
								<div className="flex flex-col gap-1 w-full" onClick={(e) => e.stopPropagation()}>
									<div className="flex items-center gap-2 w-full">
										<Input
											value={pocTitleDraft}
											onChange={(e) => setPocTitleDraft(e.target.value.slice(0, 50))}
											onBlur={() => {
												if (pocTitleDraft.trim() !== (taskData?.pocTask || '').trim()) {
													handleSavePocTitle();
												} else {
													setEditingPocTitle(false);
												}
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
													e.preventDefault();
													handleSavePocTitle();
												}
											}}
											className="h-9 text-sm flex-1 !bg-zinc-50"
											placeholder="Set your own title for this task"
											maxLength={50}
											autoFocus
										/>
										<Button size="icon" className="h-9 w-9" onMouseDown={(e) => e.preventDefault()} onClick={handleSavePocTitle} disabled={savingPocTitle}>
											<Check className="h-4 w-4" />
										</Button>
									</div>
									{taskData?.title && (
										<div className="text-xs text-sub-text-color italic mt-1">{taskData.title}</div>
									)}
								</div>
							) : (
								<div className="flex-1">
									<div className="flex items-center gap-2">
										<span className="font-semibold text-color break-words">
											{isAssigneeMine && !isCreatorMine && taskData?.pocTask ? taskData.pocTask : taskData?.title}
										</span>
										{isAssigneeMine && !isCreatorMine && !isTaskClosed && (
											<button
												type="button"
												onClick={(e) => { e.stopPropagation(); setPocTitleDraft(taskData?.pocTask || ''); setEditingPocTitle(true); }}
												className="p-1 rounded-md hover:bg-gray-100 transition-colors"
											>
												<Pencil className="h-3.5 w-3.5 text-gray-400" />
											</button>
										)}
									</div>
									{isAssigneeMine && !isCreatorMine && taskData?.pocTask && taskData.pocTask.trim() !== (taskData?.title || '').trim() && (
										<div className="text-xs text-sub-text-color italic mt-1">{taskData?.title}</div>
									)}
								</div>
							)}
						</div>
						<TaskDescriptionView description={taskData?.description} title={taskData?.title} />
					</div>

					{(() => {
						const isMeeting = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2;
						const cat = selectedCategoryID ? myCategories.find(c => c.CID === selectedCategoryID) : null;
						const showAddCategory = !cat;

						if (!isAssigneeMine && !isCreatorMine && !isMeeting && !cat && !showAddCategory) return null;

						return (
							<div className="flex flex-wrap items-center gap-2 mt-2.5">
								{(isAssigneeMine || isCreatorMine) ? (
									isImportantForMe ? (
										isTaskClosed ? (
											<span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border cursor-default"
												style={{ backgroundColor: '#bf570015', borderColor: '#bf570030', color: '#bf5700' }}>
												<ChevronsUp className="h-3.5 w-3.5 shrink-0 mr-1" color="#bf5700" />
												Important
											</span>
										) : (
											<span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border cursor-pointer"
												onClick={(e) => { e.stopPropagation(); handleTogglePocPriority(); }}
												style={{ backgroundColor: '#bf570015', borderColor: '#bf570030', color: '#bf5700' }}>
												<ChevronsUp className="h-3.5 w-3.5 shrink-0 mr-1" color="#bf5700" />
												Important
											</span>
										)
									) : (
										!isTaskClosed && (
											<span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border border-dashed border-gray-300 text-sub-text-color cursor-pointer hover:border-sahi-primary-color hover:text-sahi-primary-color"
												onClick={(e) => { e.stopPropagation(); handleTogglePocPriority(); }}>
												<ChevronsUp className="h-4 w-4" />
												Mark Important
											</span>
										)
									)
								) : (
									isMeeting && (
										<span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border cursor-default"
											style={{ backgroundColor: '#71717a15', borderColor: '#71717a30', color: '#71717a' }}>
											<Users className="h-3.5 w-3.5 shrink-0 mr-1" color="#71717a" />
											Participant
										</span>
									)
								)}

								{cat && (
									<span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border ${canEditMyCategory ? 'cursor-pointer' : 'cursor-default'}`}
										onClick={e => { e.stopPropagation(); if (canEditMyCategory) { setCategoryManagerOpen(true); setCategoryManagerInitialView('list'); } }}
										style={{ backgroundColor: cat.Clr + '15', borderColor: cat.Clr + '30', color: cat.Clr }}>
										<span className="w-2 h-2 rounded-full shrink-0 mr-1" style={{ backgroundColor: cat.Clr }} />
										{cat.CNm}
									</span>
								)}
								{showAddCategory && canEditMyCategory && (
									<span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border border-dashed border-gray-300 text-sub-text-color cursor-pointer hover:border-sahi-primary-color hover:text-sahi-primary-color"
										onClick={e => { e.stopPropagation(); setCategoryManagerOpen(true); setCategoryManagerInitialView('list'); }}>
										<Plus className="h-4 w-4" />
										Set Growth Area
									</span>
								)}
							</div>
						);
					})()}

					{/* <Badge
						className="text-[12px] px-2 py-1 mt-3 border font-semibold bg-[#0b5d1e22] text-[#0b5d1e]"
					>
						<Circle
							fill="#0b5d1e"
							color="#0b5d1e"
							className="h-4 w-4 mr-1.5 scale-90"
						/>
						Home
					</Badge> */}
				</div>

				{/* Options Card for Decision (3) and Approval (4) */}
				{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 3 && (
					<div className="bg-white rounded-lg p-3 text-left">
						{(() => {
							let parsedOptions = [];
							try {
								if (taskData.options) {
									if (typeof taskData.options === 'string') {
										try {
											parsedOptions = JSON.parse(taskData.options);
										} catch (jsonErr) {
											parsedOptions = taskData.options.split(',').map(o => o.trim());
										}
									} else {
										parsedOptions = taskData.options;
									}
								}
							} catch (e) {
								console.error("Failed to parse task options:", e);
							}

							// Filter out 'Other' (case-insensitive) from parsed options to avoid double-rendering
							parsedOptions = parsedOptions.filter(opt => {
								const txt = typeof opt === 'object' && opt !== null ? (opt.OTxt || '') : String(opt);
								return txt.toLowerCase().trim() !== 'other';
							});

							const isDecisionNoOptions = Number(taskData?.class || taskData?.Class || taskData?.taskClass) === 3 && parsedOptions.length === 0;
							const isTaskOpen = Number(taskData?.status || 1) === 1;

							return (
								<>
									<div className="flex items-center text-sub-text-color gap-2 mb-3">
										<ListTodo className="h-4 w-4 text-gray-500" />
										<span className="text-sm font-medium">
											{isDecisionNoOptions ? "Decision" : "Decision Options"}
										</span>
									</div>
									<div className="space-y-2">
										{parsedOptions.map((opt, idx) => {
											const prefix = String.fromCharCode(65 + idx); // A, B, C...
											const optText = typeof opt === 'object' && opt !== null ? (opt.OTxt || '') : String(opt);
											const isSelected = isTaskOpen
												? (completionDialogPrefill === optText || (optText === 'Approve' && completionDialogPrefill === 'Approved') || (optText === 'Disapprove' && completionDialogPrefill === 'Disapproved'))
												: ((taskData?.statusNote || '').trim() === optText.trim() || (optText.trim() === 'Approve' && (taskData?.statusNote || '').trim() === 'Approved') || (optText.trim() === 'Disapprove' && (taskData?.statusNote || '').trim() === 'Disapproved'));

											const hasPoc = taskData?.pocUserID && Number(taskData?.pocUserID) !== 0;
											const canSelectOption = hasPoc ? isAssigneeMine : true;

											if (isTaskOpen) {
												return (
													<button
														key={idx}
														type="button"
														disabled={!canSelectOption}
														onClick={() => {
															const prefillVal = optText === 'Approve' ? 'Approved' : (optText === 'Disapprove' ? 'Disapproved' : optText);
															setCompletionDialogPrefill(prefillVal);
															setSelectedOptionID(typeof opt === 'object' && opt !== null ? Number(opt.OID || 0) : 0);
															setDialogStates(prev => ({ ...prev, confirmComplete: true }));
														}}
														className={`w-full text-left p-3 rounded-lg border text-sm transition-all duration-200 flex items-center gap-2 ${isSelected
															? "bg-sahi-green-extra-light border-sahi-green text-sahi-green font-semibold shadow-sm"
															: "bg-zinc-50 border-gray-200 text-text-color hover:bg-zinc-100 hover:border-gray-300"
															} ${!canSelectOption ? 'opacity-70 cursor-not-allowed' : ''}`}
													>
														{isSelected ? (
															<div className="h-4 w-4 rounded-full border border-sahi-green flex items-center justify-center shrink-0">
																<div className="w-2 h-2 rounded-full bg-sahi-green" />
															</div>
														) : (
															<Circle className="h-4 w-4 text-gray-400 shrink-0" />
														)}
														<span>{optText}</span>
													</button>
												);
											} else {
												// Closed task - show read-only status options, highlighted if selected (no prefixes)
												return (
													<div
														key={idx}
														className={`w-full text-left p-3 rounded-lg border text-sm ${isSelected
															? "bg-sahi-green-extra-light border-sahi-green text-sahi-green font-semibold opacity-100"
															: "bg-zinc-50/40 border-zinc-100 text-gray-400"
															}`}
													>
														{optText}
													</div>
												);
											}
										})}

										{/* Other Option */}
										{(() => {
											const isOtherSelected = isTaskOpen
												? (forceShowNoteField)
												: (taskData?.statusNote && !parsedOptions.map(o => {
													const oText = typeof o === 'object' && o !== null ? (o.OTxt || '') : String(o);
													return oText.trim();
												}).includes((taskData.statusNote || '').trim()));

											const hasPoc = taskData?.pocUserID && Number(taskData?.pocUserID) !== 0;
											const canSelectOption = hasPoc ? isAssigneeMine : true;

											if (isTaskOpen) {
												return (
													<button
														type="button"
														disabled={!canSelectOption}
														onClick={() => {
															setCompletionDialogPrefill('');
															setSelectedOptionID(0);
															setForceShowNoteField(true);
															setDialogStates(prev => ({ ...prev, confirmComplete: true }));
														}}
														className={`w-full text-left p-3 rounded-lg border text-sm transition-all duration-200 flex items-center gap-2 ${isOtherSelected
															? "bg-sahi-green-extra-light border-sahi-green text-sahi-green font-semibold shadow-sm border-dashed"
															: "bg-zinc-50 border-gray-200 text-text-color hover:bg-zinc-100 hover:border-gray-300 border-dashed"
															} ${!canSelectOption ? 'opacity-70 cursor-not-allowed' : ''}`}
													>
														{!isDecisionNoOptions && (isOtherSelected ? (
															<div className="h-4 w-4 rounded-full border border-sahi-green flex items-center justify-center shrink-0">
																<div className="w-2 h-2 rounded-full bg-sahi-green" />
															</div>
														) : (
															<Circle className="h-4 w-4 text-gray-400 shrink-0" />
														))}
														<span className="text-text-color">
															{isDecisionNoOptions ? "Enter your decision" : "Other"}
														</span>
														{isOtherSelected && completionDialogPrefill}
													</button>
												);
											} else {
												// Closed task: only show "Other" block if indeed a custom option was selected
												if (!isOtherSelected) return null;
												return (
													<div className="w-full text-left p-3 rounded-lg border border-dashed border-sahi-green bg-sahi-green-extra-light text-sahi-green text-sm font-medium">
														{!isDecisionNoOptions && (
															<span className="text-sahi-green font-semibold mr-1">Other:</span>
														)}
														{taskData.statusNote}
													</div>
												);
											}
										})()}
									</div>
								</>
							);
						})()}
					</div>
				)}


				{/* Time and Assignment Section */}
				{(() => {
					const hideRemindCard = (isCreatorMine && !isAssigneeMine) || (isObserverMine && !isAssigneeMine && !isCreatorMine);
					const canEditReminder = !isTaskClosed && canEditMyCategory;
					return (
						<div className="grid grid-cols-2 gap-3">
							{isTaskClosed ? (
								<>
									{/* Given on Card */}
									<div className="col-span-1 bg-white p-3 pb-2 rounded-lg text-left flex flex-col justify-between">
										<div className="flex items-center gap-2 mb-2">
											<Calendar className="h-4 w-4 text-sub-text-color" />
											<span className="text-sm text-sub-text-color">Given on</span>
										</div>
										{taskData?.createdDT ? (
											<DateTimeDisplay
												date={taskData?.createdDT}
												label="Given"
												isRemind={true}
											/>
										) : (
											<div className="py-1 flex items-end h-full">
												<span className="text-sm text-gray-400 font-normal">-</span>
											</div>
										)}
									</div>

									{/* Completed on / Cancelled on Card */}
									<div className="col-span-1 bg-white p-3 pb-2 rounded-lg text-left flex flex-col justify-between">
										<div className="flex items-center gap-2 mb-2">
											{isCompleted ? (
												<CircleCheckBig className="h-4 w-4 text-sub-text-color" />
											) : (
												<XCircle className="h-4 w-4 text-sub-text-color" />
											)}
											<span className="text-sm text-sub-text-color">
												{isCompleted ? "Completed on" : isRejected ? "Rejected on" : "Cancelled on"}
											</span>
										</div>
										{(taskData?.statusDT || taskData?.updatedDT || taskData?.dueDT || forceCompletedDT) ? (
											<DateTimeDisplay
												date={taskData?.updatedDT || taskData?.dueDT || forceCompletedDT}
												label={isCompleted ? "Completed" : isRejected ? "Rejected" : "Cancelled"}
												color="text-text-color"
												isRemind={true}
											/>
										) : (
											<div className="py-1 flex items-end h-full">
												<span className="text-sm text-text-color">-</span>
											</div>
										)}
									</div>
								</>
							) : (
								<>
									{/* Finish by - Full width if overdue with no reminder set */}
									<button
										type="button"
										className={`${hideRemindCard ? 'col-span-2' : 'col-span-1'} w-full bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left ${canEditDue ? 'cursor-pointer' : 'cursor-default'}`}
										onClick={() => { if (canEditDue) openDueModal(); }}
									>
										<div className={`flex items-center justify-between gap-2 ${showRemindBtn ? 'mb-1.5' : 'mb-3'}`}>
											<div className="flex items-center gap-2">
												<Hourglass className="h-4 w-4 text-sub-text-color" />
												<span className="text-sm text-sub-text-color">{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2 ? 'Scheduled at' : Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 3 ? 'Decide by' : 'Finish by'}</span>
											</div>
											{canRemind && (
												<div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
													{remindedToday && (
														<span className="flex items-center text-xs text-sub-text-color whitespace-nowrap">
															Reminded on {format(new Date(taskData.lastRmdrDT), 'hh:mm aa')}
														</span>
													)}
													{showRemindBtn && (
														<Tooltip>
															<TooltipTrigger asChild>
																<span
																	role="button"
																	tabIndex={0}
																	onClick={(event) => {
																		event.stopPropagation();
																		handleRemindTask();
																	}}
																	onKeyDown={(event) => {
																		if (event.key === 'Enter' || event.key === ' ') {
																			event.preventDefault();
																			event.stopPropagation();
																			handleRemindTask();
																		}
																	}}
																	className="cursor-pointer hover:opacity-85 transition-opacity inline-flex items-center gap-1.5 select-none"
																>
																	<span className="text-xs text-sub-text-color">Remind</span>
																	<span className="inline-flex items-center justify-center bg-green-50 border border-sahi-green-light text-sahi-green p-1 rounded-md text-xs font-semibold">
																		{remindLoading ? (
																			<div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sahi-primary-color border-t-transparent inline-block" />
																		) : (
																			<BsBellFill className="h-3.5 w-3.5 text-sahi-green" />
																		)}
																	</span>
																</span>
															</TooltipTrigger>
															<TooltipContent>
																<p>Remind {taskData?.pocUserName}</p>
															</TooltipContent>
														</Tooltip>
													)}
													<Tooltip>
														<TooltipTrigger asChild>
															<span
																role="button"
																tabIndex={0}
																onClick={(event) => {
																	event.stopPropagation();
																	handleRemindWhatsApp();
																}}
																onKeyDown={(event) => {
																	if (event.key === 'Enter' || event.key === ' ') {
																		event.preventDefault();
																		event.stopPropagation();
																		handleRemindWhatsApp();
																	}
																}}
																className="cursor-pointer hover:opacity-85 transition-opacity inline-flex items-center justify-center bg-green-50 border border-sahi-green-light text-sahi-green p-1 rounded-md text-xs font-semibold select-none whitespace-nowrap"
															>
																<BsWhatsapp className="h-3.5 w-3.5 text-sahi-green" />
															</span>
														</TooltipTrigger>
														<TooltipContent>
															<p>Remind on WhatsApp</p>
														</TooltipContent>
													</Tooltip>
												</div>
											)}
											{isAssigneeMine && !isCreatorMine && !isTaskClosed && !isAdmin && (
												<span
													role="button"
													tabIndex={0}
													onClick={(event) => {
														event.stopPropagation();
														setRequestExtensionOpen(true);
													}}
													onKeyDown={(event) => {
														if (event.key === 'Enter' || event.key === ' ') {
															event.preventDefault();
															event.stopPropagation();
															setRequestExtensionOpen(true);
														}
													}}
													className="text-[13px] sm:text-[14px] underline underline-offset-4 leading-none text-muted-foreground cursor-pointer font-medium whitespace-nowrap"
												>
													Extend
												</span>
											)}
										</div>
										<DateTimeDisplay
											date={taskData?.dueDT}
											label="Due"
											color={taskData?.dueDTColor}
											isRemind={!hideRemindCard}
										/>
									</button>

									{/* Remind on card in the same row - hidden entirely once overdue with no reminder ever set */}
									{!hideRemindCard && (() => {
										const isPastReminder = taskData?.rmdrDT && new Date(taskData.rmdrDT) < new Date();
										const hasActiveReminder = taskData?.rmdrDT && !isPastReminder;
										return (
											<div
												role={canEditReminder ? "button" : undefined}
												tabIndex={canEditReminder ? 0 : undefined}
												onClick={() => { if (canEditReminder) openReminderModal(); }}
												onKeyDown={(e) => { if (canEditReminder && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openReminderModal(); } }}
												className={`col-span-1 bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left flex flex-col justify-between ${canEditReminder ? 'cursor-pointer' : ''}`}
											>
												<div className="flex items-center justify-between gap-1 mb-2">
													<div className="flex items-center gap-2">
														<Bell className="h-4 w-4 text-sub-text-color" />
														<span className="text-sm text-sub-text-color">{hasActiveReminder ? "Remind on" : "Reminder"}</span>
													</div>
													{!isMobile && hasActiveReminder && canEditReminder && (
														<button
															type="button"
															onClick={(e) => {
																e.stopPropagation();
																openReminderModal();
															}}
															className="p-1 text-gray-400 hover:text-sahi-primary-color transition-colors rounded cursor-pointer"
															title="Edit Reminder"
														>
															<Pencil className="h-3.5 w-3.5" />
														</button>
													)}
												</div>

												{hasActiveReminder ? (
													<DateTimeDisplay
														date={taskData?.rmdrDT}
														label="Reminder"
														isRemind={true}
														endAdornment={isMobile && canEditReminder && (
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	openReminderModal();
																}}
																className="p-1 text-gray-400 hover:text-sahi-primary-color transition-colors rounded cursor-pointer"
																title="Edit Reminder"
															>
																<Pencil className="h-3.5 w-3.5" />
															</button>
														)}
													/>
												) : (
													<div className="py-1 flex items-end h-full">
														{canEditReminder ? (
															<span
																className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border border-dashed border-gray-300 text-sub-text-color hover:border-sahi-primary-color hover:text-sahi-primary-color"
															>
																<Plus className="h-4 w-4" />
																Set
															</span>
														) : (
															<span className="text-sm text-gray-400 font-normal">No reminder set</span>
														)}
													</div>
												)}
											</div>
										);
									})()}
								</>
							)}
						</div>
					);
				})()}



				{/* Status - a plain text card until clicked into (or the footer "Update Status" button is
					used); only starts directly editable when the assignee opens a task with no note yet. */}
				{!(isTaskClosed && Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 3 && (!taskData?.updates || taskData?.updates?.length <= 1)) && (
					<div
						className={`bg-white rounded-lg ${!isEditingStatus && canUpdateStatus ? 'cursor-pointer' : ''}`}
						onClick={() => {
							if (isEditingStatus || !canUpdateStatus) return;
							setStatusUpdateText('');
							setIsEditingStatus(true);
							focusStatusTextarea();
						}}
					>
						<div className={isMobile ? 'p-3 pb-2' : 'p-3 pb-2'}>
							<div className="flex items-center justify-between text-sub-text-color">
								<div className="flex items-center gap-2">
									<ClipboardList className="h-4 w-4" />
									<span className="text-sm font-medium">{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2 ? 'Notes' : 'Status'}</span>
								</div>
								{taskData?.statusDT && (
									<span className="text-xs text-sub-text-color">As on {getRelativeDateText(taskData.statusDT)}</span>
								)}
							</div>

							{isEditingStatus ? (
								<div className="mt-2 mb-1 relative">
									<Textarea
										ref={statusTextareaRef}
										id="task-details-status-textarea"
										rows={2}
										value={statusUpdateText}
										onChange={(e) => setStatusUpdateText(e.target.value)}
										onFocus={(e) => {
											const len = e.currentTarget.value?.length || 0;
											e.currentTarget.setSelectionRange(len, len);
										}}
										onBlur={(e) => {
											if (!isMobile) {
												return;
											}
											if (updateStatusBtnRef.current && e.relatedTarget === updateStatusBtnRef.current) {
												return;
											}
											if (speechControlsRef.current && speechControlsRef.current.contains(e.relatedTarget)) {
												return;
											}
											const trimText = statusUpdateText.trim();
											const origText = (taskData?.statusNote || '').trim();
											if (trimText && trimText !== origText) {
												handleStatusUpdate(trimText);
											} else {
												setIsEditingStatus(false);
												setStatusUpdateText(taskData?.statusNote || '');
											}
										}}
										placeholder="e.g. Discussed contract details."
										disabled={!canUpdateStatus}
										allowResize={false}
										className="text-sm !bg-zinc-50 pr-20"
									/>
									<div ref={speechControlsRef} className="absolute right-2 top-2 z-10">
										<SpeechControls
											targetId="task-details-status-textarea"
											value={statusUpdateText}
											onChange={(val) => setStatusUpdateText(val)}
											isMobile={isMobile}
											className=""
										/>
									</div>
								</div>
							) : (
								<>
									<div className="mt-2 break-words flex items-start gap-1">
										{statusUpdateText ? (
											<span className="font-medium whitespace-pre-wrap">{statusUpdateText}</span>
										) : taskData?.status === 0 ? (
											<span className="font-medium text-muted-foreground">Cancelled</span>
										) : taskData?.status === 2 ? (
											<span className="font-medium text-muted-foreground">Completed</span>
										) : (
											<span className="font-medium text-muted-foreground">Not Started</span>
										)}
										{canUpdateStatus && (
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													setStatusUpdateText('');
													setIsEditingStatus(true);
													focusStatusTextarea();
												}}
												className="p-1 text-gray-400 hover:text-sahi-primary-color transition-colors rounded cursor-pointer"
											>
												<Plus className="h-[18px] w-[18px]" />
											</button>
										)}
									</div>
									{taskData?.updates?.length > 1 && (
										<div className="mt-1.5 mb-1.5 flex justify-start">
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													setUpdatesModalOpen(true);
												}}
												className="text-xs text-sahi-green hover:text-[var(--sahi-primary-color)] underline decoration-dotted underline-offset-4 font-normal cursor-pointer transition-colors"
											>
												{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2 ? 'All Notes' : 'All Updates'} ({taskData.updates.length})
											</button>
										</div>
									)}
								</>
							)}
						</div>
					</div >
				)}

				{/* Assigned to / Assigned by */}
				{
					(() => {
						const myUserIDs = getMyAgenciesMappedIDs('UserID');
						const isCreatorMine = myUserIDs.includes(Number(taskData?.createdUserID)) || Number(taskData?.createdUserID) === currentUserID;
						const isAssigneeMine = myUserIDs.includes(Number(taskData?.pocUserID)) || Number(taskData?.pocUserID) === currentUserID;
						const isPersonalTask = isCreatorMine && isAssigneeMine;
						const isMeeting = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2;

						if (isPersonalTask && !isMeeting) return null;

						const isApproval = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 4;
						const isDecision = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 3;
						if (isMeeting) {
							const organizer = taskData?.createdUserName || "Unknown";
							const poc = taskData?.pocUserName || "Not Assigned";
							const isSameUser = Number(taskData?.createdUserID || taskData?.CreatedUID || 0) === Number(taskData?.pocUserID || taskData?.UID || 0);

							if (isSameUser || organizer === poc) {
								return (
									<div
										role={canEdit ? "button" : undefined}
										className={`w-full bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left ${canEdit ? 'cursor-pointer' : ''}`}
										onClick={() => {
											if (canEdit) openTaskEntryWithFocus("pocUserID");
										}}
										tabIndex={canEdit ? 0 : undefined}
										onKeyDown={(event) => {
											if (canEdit && (event.key === 'Enter' || event.key === ' ')) {
												event.preventDefault();
												openTaskEntryWithFocus("pocUserID");
											}
										}}
									>
										<div className="flex items-center gap-2 mb-2">
											<User2 className="h-4 w-4 text-sub-text-color" />
											<span className="text-sm text-sub-text-color">Organized by</span>
										</div>
										<div className="flex items-center gap-2">
											<span className="font-semibold">{organizer}</span>
										</div>
									</div>
								);
							} else {
								return (
									<>
										<div
											className={`w-full bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left`}
										>
											<div className="flex items-center gap-2 mb-2">
												<User2 className="h-4 w-4 text-sub-text-color" />
												<span className="text-sm text-sub-text-color">Organized by</span>
											</div>
											<div className="flex items-center gap-2">
												<span className="font-semibold">{organizer}</span>
											</div>
										</div>

										<div
											role={canEdit ? "button" : undefined}
											className={`w-full bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left mt-2 ${canEdit ? 'cursor-pointer' : ''}`}
											onClick={() => {
												if (canEdit) openTaskEntryWithFocus("pocUserID");
											}}
											tabIndex={canEdit ? 0 : undefined}
											onKeyDown={(event) => {
												if (canEdit && (event.key === 'Enter' || event.key === ' ')) {
													event.preventDefault();
													openTaskEntryWithFocus("pocUserID");
												}
											}}
										>
											<div className="flex items-center gap-2 mb-2">
												<UserRoundArrowLeft className="h-4 w-4 text-sub-text-color" />
												<span className="text-sm text-sub-text-color">Co-host</span>
											</div>
											<div className="flex items-center gap-2">
												<span className="font-semibold">{poc}</span>
											</div>
										</div>
									</>
								);
							}
						}

						const label = isAssigneeMine
							? (isApproval ? "Requested by" : isDecision ? "Decision given by" : "Assigned by")
							: (isApproval ? "Approver" : isDecision ? "Decision by" : "Assigned to");
						const displayName = isAssigneeMine ? (taskData?.createdUserName || "Unknown") : (taskData?.pocUserName || "Not Assigned");

						return (
							<div
								role={canEdit ? "button" : undefined}
								className={`w-full bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left ${canEdit ? 'cursor-pointer' : ''}`}
								onClick={() => {
									if (canEdit) openTaskEntryWithFocus("pocUserID");
								}}
								tabIndex={canEdit ? 0 : undefined}
								onKeyDown={(event) => {
									if (canEdit && (event.key === 'Enter' || event.key === ' ')) {
										event.preventDefault();
										openTaskEntryWithFocus("pocUserID");
									}
								}}
							>
								<div className="flex items-center gap-2 mb-2">
									{isAssigneeMine ? (
										<UserRoundArrowLeft className="h-4 w-4 text-sub-text-color" />
									) : (
										<User2 className="h-4 w-4 text-sub-text-color" />
									)}
									<span className="text-sm text-sub-text-color">{label}</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="font-semibold">{displayName}</span>
								</div>
							</div>
						);
					})()
				}

				{/* Observers list */}
				{
					(() => {
						const isMeeting = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2;
						if (isMeeting) return null;
						if (isObserverMine && !isAssigneeMine && !isCreatorMine) return null;
						const obs = taskData?.observerUserIDs || taskData?.ObserverUIDs || taskData?.obsUIDs || taskData?.ObsUIDs;
						if (!obs) return null;
						const arr = Array.isArray(obs) ? obs : String(obs).split(',').map(id => Number(id.trim())).filter(Boolean);
						if (arr.length === 0) return null;

						const observerNames = arr.map(id => {
							const matched = users.find(u => Number(u.userID) === Number(id));
							return matched?.name || `User #${id}`;
						}).join(', ');

						const dynamicLabel = isMeeting ? 'Attendees' : 'Observers';

						return (
							<div
								role={canEdit && !isMeeting ? "button" : undefined}
								className={`w-full bg-white ${isMobile ? 'p-3 pb-2' : 'p-3 pb-2'} rounded-lg text-left ${canEdit && !isMeeting ? 'cursor-pointer' : ''}`}
								onClick={() => {
									if (canEdit && !isMeeting) openTaskEntryWithFocus("observerUserIDs");
								}}
								tabIndex={canEdit && !isMeeting ? 0 : undefined}
								onKeyDown={(event) => {
									if (canEdit && !isMeeting && (event.key === 'Enter' || event.key === ' ')) {
										event.preventDefault();
										openTaskEntryWithFocus("observerUserIDs");
									}
								}}
							>
								<div className="flex items-center gap-2 mb-2">
									<Users className="h-4 w-4 text-sub-text-color" />
									<span className="text-sm text-sub-text-color">{dynamicLabel}</span>
								</div>

								<div className="flex items-center gap-2">
									<span className="font-semibold">{observerNames}</span>
								</div>
							</div>
						);
					})()
				}

				{/* Linked Threads and Contracts */}
				{
					(taskData?.linkedThreads?.length > 0 || taskData?.linkedCtrcs?.length > 0) && (
						<div className="bg-white p-3 sahi-radius">
							<div className="space-y-3">

								{/* Linked Threads */}
								{taskData?.linkedThreads?.length > 0 && (
									<div>
										<div className="text-sm text-sub-text-color mb-2">
											Linked {isInqOnly
												? taskData.linkedThreads.length > 1
													? 'Inquiries'
													: 'Inquiry'
												: 'Threads'}
										</div>

										<div className="flex flex-wrap gap-2">
											{taskData.linkedThreads.map(thread => (
												<HoverCard key={thread.threadID}>
													<HoverCardTrigger asChild>
														<div className={`${tskDetail.threadTag} cursor-pointer transition-colors`}>
															<ThreadSahiIcon tID={thread.typeID} className="h-4 w-4" />
															<span className="font-semibold break-words">
																{thread.name}
															</span>
														</div>
													</HoverCardTrigger>

													<HoverCardContent
														className="bg-white sahi-radius shadow-2xl p-3 w-30 text-left"
														side="top"
													>
														<h4 className="text-sm font-bold mb-2">
															{getThreadNameFromTypeID(thread.typeID, threadTypes)}
														</h4>

														{thread.dueDT && (
															<div>
																<span className="text-sub-text-color">Due on: </span>
																<span>{getRelativeDateText(thread.dueDT)}</span>
															</div>
														)}

														{thread.sinceDT && (
															<div>
																<span className="text-sub-text-color">Since: </span>
																<span>{getRelativeDateText(thread.sinceDT)}</span>
															</div>
														)}
													</HoverCardContent>
												</HoverCard>
											))}
										</div>
									</div>
								)}

								{/* Linked Contracts */}
								{taskData?.linkedCtrcs?.length > 0 && (
									<div>
										<div className="text-sm text-sub-text-color mb-2">
											Linked {taskData.linkedCtrcs.length > 1 ? 'Contracts' : 'Contract'}
										</div>

										<div className="flex flex-wrap gap-2">
											{taskData.linkedCtrcs.map(ctrc => (
												<HoverCard key={ctrc?.ctrcID}>
													<HoverCardTrigger asChild>
														<div className={`${tskDetail.threadTag} cursor-pointer transition-colors`}>
															<FileText
																className={`h-4 w-4 ${ctrc?.isSeller
																	? 'text-sahi-orange'
																	: 'text-sahi-green'
																	}`}
															/>
															<span className="font-semibold break-words">
																{ctrc?.type}
															</span>
														</div>
													</HoverCardTrigger>

													<HoverCardContent
														className="bg-white sahi-radius shadow-2xl p-3 w-30 text-left"
														side="top"
													>
														<h4 className="text-sm font-bold mb-2">
															{ctrc?.type}
														</h4>

														{ctrc?.expiryDT && (
															<div>
																<span className="text-sub-text-color">Due on: </span>
																<span>{getRelativeDateText(ctrc?.expiryDT)}</span>
															</div>
														)}

														{ctrc?.startDT && (
															<div>
																<span className="text-sub-text-color">Start: </span>
																<span>{getRelativeDateText(ctrc?.startDT)}</span>
															</div>
														)}
													</HoverCardContent>
												</HoverCard>
											))}
										</div>
									</div>
								)}

							</div>
						</div>
					)
				}

				{/* Meeting Attendance Panels - hidden for pure observers */}
				{Number(taskData?.class || taskData?.Class || taskData?.taskClass) === 2 && !(isObserverMine && !isAssigneeMine && !isCreatorMine) && (() => {
					const observerUActions = taskData?.observerUActions || taskData?.obsUActions || taskData?.ObsUActions || [];
					const attendingList = observerUActions.filter(r => r.Sts === 0);
					const notAttendingList = observerUActions.filter(r => r.Sts === 2 || r.Sts === 1);

					if (attendingList.length === 0 && notAttendingList.length === 0) return null;

					return (
						<div className="space-y-3 mt-4">
							{/* Attending Panel */}
							{attendingList.length > 0 && (
								<div className="bg-white p-3 rounded-lg text-left">
									<div className="flex items-center gap-2 mb-3">
										<UsersRound className="h-4 w-4 text-sub-text-color" />
										<span className="text-sm text-sub-text-color">Participants</span>
									</div>
									<div className="flex flex-wrap gap-2">
										{attendingList.map((response) => {
											const userObj = users.find(u => Number(u.userID || u.UserID) === Number(response.OUID));
											const userName = userObj ? (userObj.name || userObj.Name) : `User (${response.OUID})`;
											return (
												<div
													key={response.OUID}
													className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-zinc-100 text-zinc-800 border border-zinc-200"
												>
													{userName}
												</div>
											);
										})}
									</div>
								</div>
							)}

							{/* Not Attending Panel */}
							{notAttendingList.length > 0 && (
								<div className="bg-white p-3 rounded-lg text-left">
									<div className="flex items-center gap-2 mb-3">
										<UserRoundX className="h-4 w-4 text-sub-text-color" />
										<span className="text-sm text-sub-text-color">Not Joining</span>
									</div>
									<div className="flex flex-wrap gap-2">
										{notAttendingList.map((response) => {
											const userObj = users.find(u => Number(u.userID || u.UserID) === Number(response.OUID));
											const userName = userObj ? (userObj.name || userObj.Name) : `User (${response.OUID})`;
											return (
												<div
													key={response.OUID}
													className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-zinc-100 text-zinc-800 border border-zinc-200 text-left"
												>
													<span>{userName}</span>
													{response.Rsn && (
														<Popover>
															<PopoverTrigger asChild>
																<button
																	type="button"
																	className="p-0.5 hover:bg-zinc-200 rounded-full cursor-pointer focus:outline-none transition-colors inline-flex items-center justify-center shrink-0"
																	onClick={(e) => {
																		e.stopPropagation();
																	}}
																>
																	<HelpCircle className="h-3.5 w-3.5 text-sub-text-color hover:text-zinc-700" />
																</button>
															</PopoverTrigger>
															<PopoverContent
																className="max-w-[200px] w-auto whitespace-normal bg-zinc-800 text-white text-xs p-2 rounded shadow-md border-0 z-[10005] text-left"
																side="top"
																align="start"
															>
																<p>{response.Rsn}</p>
															</PopoverContent>
														</Popover>
													)}
												</div>
											);
										})}
									</div>
								</div>
							)}
						</div>
					);
				})()}

			</div >
		);
	};

	const ActionButtons = (overrideTaskData = null) => {
		const taskData = overrideTaskData ?? _primaryTaskData;
		const myUserIDs = getMyAgenciesMappedIDs('UserID');
		const isCreatorMine = myUserIDs.includes(Number(taskData?.createdUserID)) || Number(taskData?.createdUserID) === currentUserID;
		const isAssigneeMine = myUserIDs.includes(Number(taskData?.pocUserID)) || Number(taskData?.pocUserID) === currentUserID;
		const isCreatorOrAdminBase = isCreatorMine;
		const isAssigneeOnly = isAssigneeMine && !isCreatorOrAdminBase;
		const isOpen = taskData?.status === 1;
		// const isMarkedDone = taskData?.status === 3; // No longer used now that Mark as Done is disabled
		const isTaskClosed = forceTaskClosed !== false || taskData?.status === 2 || taskData?.status === 0 || taskData?.status === 4 || taskData?.status === 3;
		const isCreatorOrAdmin = isCreatorMine && !isTaskClosed;

		const handleUpdateClick = () => {
			if (!isEditingStatus) {
				setStatusUpdateText('');
				setIsEditingStatus(true);
				focusStatusTextarea();
				return;
			}
			if (!statusUpdateText.trim()) {
				toast.error("Please enter a status update.");
				focusStatusTextarea();
				return;
			}
			handleStatusUpdate();
		};
		const showUpdateStatusBtn = isEditingStatus;

		return (
			<div className="flex flex-wrap gap-2">
				{showUpdateStatusBtn && (
					<Button ref={updateStatusBtnRef} onClick={handleUpdateClick} className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green sm:flex-none sm:min-w-[110px]">
						<Pencil className="h-4 w-4" /> Update Status
					</Button>
				)}

				{!isEditingStatus && isAssigneeOnly && isOpen && !isTaskClosed && Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) !== 4 && taskData?.canComplete == true && (
					<Button
						onClick={() => setDialogStates(p => ({ ...p, confirmComplete: true }))}
						className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green sm:flex-none sm:min-w-[110px]">
						<Check className="h-4 w-4" /> Complete
					</Button>
				)}

				{!isEditingStatus && isAssigneeOnly && isOpen && !isTaskClosed && Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) !== 4 && (
					<Button onClick={() => setDialogStates(p => ({ ...p, confirmReject: true }))} className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red sm:flex-none sm:min-w-[110px] text-primary-foreground">
						<Ban className="h-4 w-4" /> Reject
					</Button>
				)}

				{/* Assignee Approver: Approve, Disapprove, and Other actions for Approval tasks */}
				{!isEditingStatus && isAssigneeOnly && isOpen && !isTaskClosed && Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 4 && (
					<>
						<Button
							onClick={() => {
								setCompletionDialogPrefill('Approved');
								setSelectedOptionID(1);
								setDialogStates(p => ({ ...p, confirmComplete: true }));
							}}
							className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green sm:flex-none sm:min-w-[110px]">
							<Check className="h-4 w-4" /> Approve
						</Button>
						<Button
							onClick={() => {
								setCompletionDialogPrefill('Disapproved');
								setSelectedOptionID(2);
								setDialogStates(p => ({ ...p, confirmComplete: true }));
							}}
							className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red sm:flex-none sm:min-w-[110px] text-primary-foreground">
							<Ban className="h-4 w-4" /> Disapprove
						</Button>
						<Button
							onClick={() => {
								setCompletionDialogPrefill('');
								setSelectedOptionID(3);
								setForceShowNoteField(true);
								setDialogStates(p => ({ ...p, confirmComplete: true }));
							}}
							variant="outline"
							className="sahi-secondary-button sm:flex-none sm:min-w-[100px]">
							Other
						</Button>
					</>
				)}

				{/* Creator/Admin: Complete (always allowed for the creator, regardless of canComplete) + Cancel */}
				{!isEditingStatus && isCreatorOrAdmin && !isTaskClosed && (
					<>
						{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) !== 4 && (
							<Button
								onClick={() => setDialogStates(p => ({ ...p, confirmComplete: true }))}
								className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green sm:flex-none sm:min-w-[110px]">
								<Check className="h-4 w-4" /> Complete
							</Button>
						)}
						<Button onClick={() => setDialogStates(p => ({ ...p, confirmCancel: true }))} className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red sm:flex-none sm:min-w-[110px] text-primary-foreground">
							<Ban className="h-4 w-4" /> Cancel
						</Button>
					</>
				)}

				{isCreatorMine && isTaskClosed && Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) !== 3 && Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) !== 4 && (
					<Button
						onClick={() => setDialogStates(p => ({ ...p, confirmReopen: true }))}
						className="bg-sahi-dark-blue border hover:border-sahi-dark-blue hover:text-sahi-dark-blue sm:flex-none sm:min-w-[110px]">
						<RotateCw className="h-4 w-4" /> Reopen
					</Button>
				)}

				{!isEditingStatus && isObserverMine && !isAssigneeMine && !isCreatorMine && !isTaskClosed && (
					<Button
						onClick={() => setDialogStates(p => ({ ...p, confirmCancel: true }))}
						className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red sm:flex-none sm:min-w-[110px] text-primary-foreground"
					>
						<Ban className="h-4 w-4" /> Reject
					</Button>
				)}

				{!isMobile && (
					<Button variant="outline" onClick={onClose} className="sahi-secondary-button sm:flex-none sm:min-w-[100px]">
						<X className="h-4 w-4" /> Close
					</Button>
				)}

				{taskData.connID !== 0 && (
					<div className="flex gap-2">
						{taskData.connectionData?.mobile && (
							<Button
								className="bg-sahi-dark-blue px-2.5 border  hover:text-sahi-dark-blue"
								onClick={() =>
									handlePhoneClickAction(
										taskData.connectionData,
										isDesktop,
										"",
										taskData.connID
									)
								}
							>
								<MdPhone className="h-4 w-4" />
							</Button>
						)}

						{taskData.connectionData?.mobile && (
							<Button
								className="bg-sahi-dark-blue px-2.5 border hover:text-sahi-dark-blue"
								onClick={(e) => {
									e.stopPropagation();
									setTemplateDialogOpen(true);
								}}
							>
								<BsWhatsapp className="h-4 w-4" />
							</Button>
						)}

						{taskData.connectionData?.email && taskData.type === "Email" && (
							<Button
								className="bg-sahi-dark-blue px-2.5 border hover:text-sahi-dark-blue"
								onClick={(e) => {
									e.stopPropagation();
									window.open(
										createEmailLink(taskData.connectionData.email)
									);
								}}
							>
								<Mail className="h-4 w-4" />
							</Button>
						)}
					</div>
				)}
			</div>
		);
	};

	return (
		<TooltipProvider>
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent
					className={`${isMobile ? "w-full h-full max-w-full max-h-full sm:!h-full sm:!max-w-full sm:!max-h-full sm:!top-0 sm:!left-0 sm:!translate-x-0 sm:!translate-y-0 sm:!rounded-none p-0 flex flex-col gap-0 bg-[var(--background)] overflow-hidden" : `${show4Buttons ? 'sm:!max-w-[40vw]' : 'sm:!max-w-[33vw]'} sm:!w-[50vw] sm:!min-h-[26vh] sm:!max-h-[72vh] flex flex-col gap-0 bg-[var(--background)] overflow-visible`}`}
				>
					{/* HEADER: always static, never translates */}
					<DialogHeader className={isMobile ? "border-b px-3 flex-shrink-0" : tskDetail.header}>
						<div className="flex items-center justify-between w-full">
							{isMobile ? (
								<>
									<div className="flex items-center gap-2 flex-1 min-w-0">
										<DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
											<ArrowLeft className="h-[24px] w-[24px]" />
											<span className="sr-only">Back</span>
										</DialogClose>
										<DialogTitle className="flex items-center gap-2">
											<div className="truncate">
												{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2 ? "Meeting" : Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 3 ? "Decision" : Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 4 ? "Approval" : (taskData?.type && taskData.type !== "-" ? `Task (${taskData.type})` : "Task Details")}
											</div>
										</DialogTitle>
									</div>
									{canEdit && <Button variant="ghost" size="sm" onClick={() => openTaskEntryWithFocus("taskName")} className="h-8 w-8 p-0">
										<Pencil className="h-4 w-4" />
									</Button>}
								</>
							) : (
								<>
									<DialogTitle className="text-xl">
										{Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2 ? "Meeting" : Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 3 ? "Decision" : Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 4 ? "Approval" : (taskData?.type && taskData.type !== "-" ? `Task (${taskData.type})` : "Task Details")}
									</DialogTitle>
									<div className="flex items-center gap-2">
										{canEdit && <Button variant="ghost" size="sm" onClick={() => openTaskEntryWithFocus("taskName")} className="h-8 w-8 p-0  sm:text-text-color sm:hover:bg-gray-200">
											<Pencil className="h-4 w-4" />
										</Button>}
										<DialogClose asChild><Button variant="ghost" className="h-8 w-8 p-0"><X className="h-4 w-4" /></Button></DialogClose>
									</div>
								</>
							)}
						</div>
					</DialogHeader>

					{(() => {
						const hasCarousel = isMobile && mobileActiveTasks && mobileActiveTasks.length > 1;

						if (hasCarousel) {
							return (
								<div
									className="flex-1 relative overflow-hidden"
									onTouchStart={handleTouchStart}
									onTouchMove={handleTouchMove}
									onTouchEnd={handleTouchEnd}
								>
									{(() => {
										const swipeAbs = Math.abs(swipeOffset);
										const screenWidth = window.innerWidth;

										const progress = Math.min(swipeAbs / screenWidth, 1);
										const midY = 120;

										const exitY = progress * midY;
										const enterY = (1 - progress) * midY;

										const spring = (isDragging || disableTransition) ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease';

										return (
											<>
												{swipeOffset !== 0 && (
													<div
														className="absolute inset-0 overflow-y-auto p-3 pointer-events-none"
														style={{
															transform: `translateX(calc(-100vw + ${swipeOffset}px)) translateY(${enterY}px)`,
															transition: spring,
															opacity: swipeOffset > 10 ? 1 : 0,
														}}
													>
														<div className="space-y-4">
															{prevAdjacentData ? (
																<>
																	{TaskInformationSection(prevAdjacentData)}
																	<div>{ActionButtons(prevAdjacentData)}</div>
																</>
															) : (
																<div className="flex h-32 items-center justify-center text-sub-text-color text-sm">No previous task</div>
															)}
														</div>
													</div>
												)}

												<div
													className="absolute inset-0 overflow-y-auto p-3"
													style={{
														transform: `translateX(${swipeOffset}px) translateY(${exitY}px)`,
														transition: spring,
													}}
												>
													<div className="space-y-4">
														{TaskInformationSection()}
														<div>{ActionButtons()}</div>
													</div>
												</div>

												{swipeOffset !== 0 && (
													<div
														className="absolute inset-0 overflow-y-auto p-3 pointer-events-none"
														style={{
															transform: `translateX(calc(100vw + ${swipeOffset}px)) translateY(${enterY}px)`,
															transition: spring,
															opacity: swipeOffset < -10 ? 1 : 0,
														}}
													>
														<div className="space-y-4">
															{nextAdjacentData ? (
																<>
																	{TaskInformationSection(nextAdjacentData)}
																	<div>{ActionButtons(nextAdjacentData)}</div>
																</>
															) : (
																<div className="flex h-32 items-center justify-center text-sub-text-color text-sm">No next task</div>
															)}
														</div>
													</div>
												)}
											</>
										);
									})()}
								</div>
							);
						}

						if (isMobile) {
							return (
								<div className="flex-1 overflow-y-auto p-3">
									<div className="space-y-4">
										{TaskInformationSection()}
										<div>{ActionButtons()}</div>
									</div>
								</div>
							);
						}

						return (
							<>
								<div className={tskDetail.gridMain}>
									<div className={tskDetail.leftCol}>
										{TaskInformationSection()}
									</div>
								</div>
								<div className="bg-background rounded-lg px-4 pb-4">
									{ActionButtons()}
								</div>
							</>
						);
					})()}
				</DialogContent>

				<TaskCategoryManager
					open={categoryManagerOpen}
					onOpenChange={(val) => { setCategoryManagerOpen(val); if (!val) setCategoryManagerInitialView('list'); }}
					categories={myCategories}
					initialView={categoryManagerInitialView}
					selectedCID={selectedCategoryID}
					onSelect={async (cid) => {
						// const taskAgencyID = Number(taskData?.AgencyID || 0);
						// if (taskAgencyID) apiService.setAgencyContext(taskAgencyID);
						try {
							await manageTaskAPI({ ...formatTaskForAPI(taskData), myCategory: cid }, 'CATEDIT');
						} catch (err) {
							toast.error(err.message || 'Failed to update category.');
						} finally {
							apiService.clearAgencyContext();
						}
						setCategoryManagerOpen(false);
						setCategoryManagerInitialView('list');
					}}
					onCategoriesChange={setMyCategories}
				/>

				<TaskEntry
					key="task-details-module-task-entry"
					dialogTitle={taskEntryMode === 'repeat' ? 'Add Task' : 'Edit Task'}
					triggerButton={false}
					open={dialogStates.taskEntry}
					onOpenChange={handleTaskEntryOpenChange}
					prefilledData={taskEntryMode === 'repeat' ? repeatPrefillData : formatTaskForAPI(taskData)}
					initialFocusField={taskEntryFocusTarget}
				/>

				<TemplateDialog
					isOpen={templateDialogOpen}
					onClose={() => { setTemplateDialogOpen(false) }}
					connectionData={taskData.connectionData}
					onRecordAdded={null}
					connID={taskData.connID}
				/>

				<TaskCompleteDialog
					open={dialogStates.confirmComplete}
					onOpenChange={(open) => {
						setDialogStates(prev => ({ ...prev, confirmComplete: open }));
						if (!open) {
							setCompletionDialogPrefill('');
							setSelectedOptionID(0);
							setForceShowNoteField(false);
						}
					}}
					task={taskData}
					shareByWhatsApp={shareActionByWhatsApp}
					onWhatsAppShareChange={setShareActionByWhatsApp}
					initialStatusNote={completionDialogPrefill}
					forceShowNoteField={forceShowNoteField}
					onAction={(val, repeat, statusNote) => {
						if (val === 'complete') {
							handleCompleteTask(false, false, repeat, statusNote);
						} else if (val === 'record') {
							pendingRepeatAfterRecordRef.current = repeat;
							pendingStatusNoteRef.current = statusNote;
							handleCompleteTask(true, false, repeat, statusNote);
						}
					}}
				/>
				{/* Mark as Done confirm dialog is disabled for now (being reworked into the Status update flow).
				<AlertBox
					open={dialogStates.confirmMarkDone}
					onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmMarkDone: open }))}
					title="Mark as Done"
					content={
						<div>
							<p className="mb-2"><span className="font-medium text-sahi-primary-color">{taskData?.title}</span></p>
							<p className="text-sm text-sub-text-color">This will notify the task creator to review and complete or cancel.</p>
						</div>
					}
					className={isDesktop ? "w-[400px]" : ""}
					actions={[
						{ label: "No", value: 'no', variant: 'cancel', icon: <X className="h-4 w-4" /> },
						{ label: "Mark as Done", value: 'yes', icon: <Check className="h-4 w-4" /> }
					]}
					onAction={(val) => { if (val === 'yes') handleMarkAsDone() }}
				/> */}
				<TaskCompleteDialog
					mode="reject"
					open={dialogStates.confirmReject}
					onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmReject: open }))}
					task={taskData}
					shareByWhatsApp={shareActionByWhatsApp}
					onWhatsAppShareChange={setShareActionByWhatsApp}
					initialStatusNote={statusUpdateText}
					onAction={(val, statusNote) => { if (val === 'yes') handleRejectTask(statusNote) }}
				/>
				<TaskCompleteDialog
					mode={isObserverMine && !isAssigneeMine && !isCreatorMine ? "reject" : "cancel"}
					open={dialogStates.confirmCancel}
					onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCancel: open }))}
					task={taskData}
					shareByWhatsApp={shareActionByWhatsApp}
					onWhatsAppShareChange={setShareActionByWhatsApp}
					className={isDesktop ? "w-[400px]" : ""}
					initialStatusNote=""
					onAction={(val, statusNote) => { if (val === 'yes') handleCancelTask(statusNote) }}
				/>
				<AlertBox
					open={dialogStates.confirmReopen}
					onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmReopen: open }))}
					title="Reopen Task"
					content={(() => {
						const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
						const isAssigneeMine = myUserIDs.includes(Number(taskData?.pocUserID)) || Number(taskData?.pocUserID) === currentUserID;
						const showWhatsAppOption = !isAssigneeMine;
						return (
							<div className="space-y-3">
								{!isAssigneeMine && taskData?.pocUserName && (
									<p className="text-sm text-sub-text-color">This will reopen the task and notify {taskData.pocUserName}.</p>
								)}
								{showWhatsAppOption && (
									<label className="flex items-center gap-2 mt-2 cursor-pointer select-none text-left w-full justify-start">
										<input
											type="checkbox"
											checked={shareActionByWhatsApp}
											onChange={(e) => setShareActionByWhatsApp(e.target.checked)}
											className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
										/>
										<span className="text-sm text-text-color font-medium">Send by WhatsApp</span>
									</label>
								)}
							</div>
						);
					})()}
					className={isDesktop ? "w-[400px]" : ""}
					actions={[
						{ label: "No", value: 'no', variant: 'cancel', icon: <X className="h-4 w-4" /> },
						{ label: "Reopen", value: 'yes', icon: <RotateCw className="h-4 w-4" /> }
					]}
					onAction={(val) => { if (val === 'yes') handleReopenTask() }}
				/>
				{(() => {
					const baseDate = (taskData?.dueDT && new Date(taskData.dueDT) > new Date())
						? new Date(taskData.dueDT)
						: new Date();

					const options = [
						{ label: "10 Days", date: addDays(baseDate, 10) },
						{ label: "15 Days", date: addDays(baseDate, 15) },
						{ label: "30 Days", date: addDays(baseDate, 30) },
					];

					const isCustomSelected = !!customExtensionDate && !!selectedExtensionDate && format(selectedExtensionDate, 'yyyy-MM-dd') === format(customExtensionDate, 'yyyy-MM-dd');

					const handleCloseExtension = () => {
						setRequestExtensionOpen(false);
						setCustomExtensionDate(null);
						setSelectedExtensionDate(null);
						setExtensionCalendarOpen(false);
					};

					const extensionOptions = (
						<div className="space-y-3">
							<div className="grid grid-cols-2 gap-2.5">
								{options.map((opt) => {
									const isSelected = selectedExtensionDate && format(selectedExtensionDate, 'yyyy-MM-dd') === format(opt.date, 'yyyy-MM-dd') && !customExtensionDate;
									return (
										<button
											key={opt.label}
											type="button"
											onClick={() => {
												setSelectedExtensionDate(opt.date);
												setCustomExtensionDate(null);
											}}
											className={cn(
												"flex flex-col items-start justify-center p-2.5 sahi-radius border transition-all cursor-pointer group text-left outline-none",
												isSelected
													? "border-zinc-400 bg-zinc-50"
													: "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 active:scale-[0.98]"
											)}
										>
											<span className="font-semibold text-sm text-text-color">
												{opt.label}
											</span>
											<span className="text-xs text-sub-text-color mt-0.5">
												Till {formatTaskDisplayDate(opt.date)}
											</span>
										</button>
									);
								})}

								<Popover open={extensionCalendarOpen} onOpenChange={setExtensionCalendarOpen}>
									<PopoverTrigger asChild>
										<button
											type="button"
											className={cn(
												"flex flex-col items-start justify-center p-2.5 sahi-radius border transition-all cursor-pointer group text-left outline-none",
												isCustomSelected
													? "border-zinc-400 bg-zinc-50"
													: "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 active:scale-[0.98]"
											)}
										>
											<span className="font-semibold text-sm text-text-color flex items-center gap-1.5">
												<Calendar className="h-3.5 w-3.5 text-zinc-500" />
												Custom date
											</span>
											<span className="text-xs text-sub-text-color mt-0.5">
												{customExtensionDate ? formatTaskDisplayDate(customExtensionDate) : "Pick from calendar"}
											</span>
										</button>
									</PopoverTrigger>
									<PopoverContent
										className="w-auto p-0 bg-white border border-zinc-200 sahi-radius shadow-lg z-[10030]"
										align="end"
										sideOffset={5}
									>
										<CalendarComponent
											mode="single"
											selected={customExtensionDate}
											captionLayout="dropdown"
											fromYear={new Date().getFullYear()}
											toYear={new Date().getFullYear() + 5}
											className="rdp-dropdown-custom"
											fixedWeeks={true}
											onSelect={(date) => {
												if (date) {
													setCustomExtensionDate(date);
													setSelectedExtensionDate(date);
													setExtensionCalendarOpen(false);
												}
											}}
											disabled={(date) => {
												const today = new Date();
												today.setHours(0, 0, 0, 0);
												return date < today;
											}}
										/>
									</PopoverContent>
								</Popover>
							</div>
						</div>
					);

					const ExtensionFooter = (
						<div className="flex items-center justify-start gap-2.5 px-4 pb-4 pt-3 shrink-0">
							<Button
								onClick={() => {
									if (!selectedExtensionDate) {
										toast.error("Please select an extension date.");
										return;
									}
									handleApplyExtensionDate(selectedExtensionDate);
								}}
								disabled={!selectedExtensionDate}
								className="sahi-primary-button flex-1 sm:flex-none"
							>
								<Check className="h-4 w-4" strokeWidth={3} /> Confirm
							</Button>
							<Button
								variant="outline"
								onClick={handleCloseExtension}
								className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[90px]"
							>
								<X className="w-3.5 h-3.5" /> Close
							</Button>
						</div>
					);

					if (isMobile) {
						return (
							<Drawer open={requestExtensionOpen} onOpenChange={(open) => { if (!open) handleCloseExtension(); else setRequestExtensionOpen(true); }}>
								<DrawerContent
									className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
									style={{ maxHeight: `${dueVvHeight * 0.85}px`, height: 'auto' }}
								>
									<div className="flex flex-col" style={{ maxHeight: `${dueVvHeight * 0.85}px` }}>
										<div className="flex items-center justify-between px-4 h-16 shrink-0">
											<div className="flex gap-4 items-center">
												<button type="button" tabIndex={-1} onClick={handleCloseExtension}
													className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500">
													<ArrowLeft className="h-[24px] w-[24px]" />
												</button>
												<p className="text-lg font-semibold text-gray-900">Request Extension</p>
											</div>
										</div>
										<div className="overflow-y-auto custom-scroll flex-1 px-4 pb-4">
											{extensionOptions}
										</div>
										{ExtensionFooter}
									</div>
								</DrawerContent>
							</Drawer>
						);
					}

					return (
						<Dialog open={requestExtensionOpen} onOpenChange={(open) => { if (!open) handleCloseExtension(); else setRequestExtensionOpen(true); }}>
							<DialogContent className="sm:!max-w-[400px] flex flex-col gap-0 bg-[var(--background)] p-0 overflow-visible">
								<DialogHeader className="border-b px-4 py-3 flex-row items-center justify-between space-y-0">
									<DialogTitle>Request Extension</DialogTitle>
									<DialogClose asChild>
										<Button variant="ghost" className="h-8 w-8 p-0"><X className="h-4 w-4" /></Button>
									</DialogClose>
								</DialogHeader>
								<div className="px-4 py-3">
									{extensionOptions}
								</div>
								{ExtensionFooter}
							</DialogContent>
						</Dialog>
					);
				})()}
				<RecordEntry
					triggerButton={false}
					open={dialogStates.recordEntry}
					onOpenChange={(open) => setDialogStates(prev => ({ ...prev, recordEntry: open }))}
					prefilledData={{
						connID: taskData?.connID,
						connectionLocked: true,
						bizName: taskData?.connectionData?.bizName || taskData?.connectionData?.contactName,
						threadIDs: taskData?.threadIDs ? taskData.threadIDs.split(',') : [],
					}}
					selectedTaskForRecord={{ ...formatTaskForAPI(taskData), repeatTask: pendingRepeatAfterRecordRef.current, statusNote: pendingStatusNoteRef.current }}
					onRecordAdded={() => {
						handleCompleteTask(false, true);
						pendingRepeatAfterRecordRef.current = false;
						pendingStatusNoteRef.current = "";
					}}
					shouldOpenTaskEntry={true}
				/>

				{/* Connection Details Dialogs */}
				{!openedFromConnectionDetails && taskData?.connID !== 0 && (
					isMobile ? (
						<ConnectionDetailsMobile
							isOpen={dialogStates.connectionDetails}
							onClose={() => setDialogStates(prev => ({ ...prev, connectionDetails: false }))}
							connID={taskData.connID}
						/>
					) : (
						<ConnectionDetails
							isOpen={dialogStates.connectionDetails}
							onClose={() => setDialogStates(prev => ({ ...prev, connectionDetails: false }))}
							connID={taskData.connID}
						/>
					)
				)}

				{(() => {
					const reminderTitle = taskData?.rmdrDT ? "Edit Reminder" : "Set Reminder";
					const desktopLabel = (
						<div className="flex items-center justify-between mb-1">
							<Label className="text-sm font-medium text-gray-700">Reminder Date & Time</Label>
							{taskData?.rmdrDT && (
								<Tooltip>
									<TooltipTrigger asChild>
										<button type="button" onClick={() => handleSaveReminder(true)} disabled={savingReminder}
											className="text-sahi-red hover:opacity-70 transition-opacity">
											<Trash2 className="h-3.5 w-3.5" />
										</button>
									</TooltipTrigger>
									<TooltipContent>
										<p>Remove Reminder</p>
									</TooltipContent>
								</Tooltip>
							)}
						</div>
					);
					const isTaskOverdueForReminder = taskData?.dueDT && new Date(taskData.dueDT) < new Date();
					const dateMinMax = {
						minDate: (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })(),
						maxDate: (taskData?.dueDT && !isTaskOverdueForReminder) ? (() => { const d = new Date(taskData.dueDT); d.setHours(23, 59, 59, 999); return d; })() : undefined,
					};
					const ReminderFields = (isDesktopView) => (
						<div>
							{desktopLabel}
							<div className="flex flex-nowrap items-center gap-3 mt-1.5">
								<div className="flex-1 min-w-0">
									<DatePicker
										value={reminderDate}
										onChange={handleReminderDateChange}
										placeholder="Select Date"
										minDate={dateMinMax.minDate}
										maxDate={dateMinMax.maxDate}
									/>
								</div>
								<div className="flex-1 min-w-0">
									<TimeInputWithDialog
										value={reminderTime}
										onChange={handleReminderTimeChange}
										placeholder="Select Time"
										fieldID="rmdrTimePicker"
										canClose={false}
										hideLabel={true}
									/>
								</div>
							</div>
						</div>
					);
					const ReminderFooter = (
						<div className="flex gap-3 px-4 pb-4 pt-4 border-t border-zinc-100 shrink-0">
							<Button onClick={() => handleSaveReminder(false)} disabled={savingReminder} className="sahi-primary-button flex-1 sm:flex-none">
								<Check className="h-4 w-4" strokeWidth={3} /> {savingReminder ? "Saving..." : "Save Reminder"}
							</Button>
							<Button variant="outline" onClick={() => setReminderModalOpen(false)} disabled={savingReminder} className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]">
								<X className="w-3 h-3" /> Close
							</Button>
						</div>
					);

					if (isMobile) {
						return (
							<Drawer open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
								<DrawerContent
									className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
									style={{ maxHeight: `${reminderVvHeight * 0.85}px`, height: 'auto' }}
								>
									<div className="flex flex-col" style={{ maxHeight: `${reminderVvHeight * 0.85}px` }}>
										<div className="flex items-center justify-between px-4 h-16 shrink-0">
											<div className="flex gap-4 items-center">
												<button type="button" tabIndex={-1} onClick={() => setReminderModalOpen(false)}
													className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500">
													<ArrowLeft className="h-[24px] w-[24px]" />
												</button>
												<p className="text-lg font-semibold text-gray-900">{reminderTitle}</p>
											</div>
											{taskData?.rmdrDT && (
												<Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-sahi-red hover:bg-sahi-red-light"
													onClick={() => handleSaveReminder(true)} disabled={savingReminder} title="Remove Reminder">
													<Trash2 className="h-4 w-4" />
												</Button>
											)}
										</div>
										<div className="overflow-y-auto custom-scroll flex-1 px-4 py-1">
											{ReminderFields(false)}
										</div>
										{ReminderFooter}
									</div>
								</DrawerContent>
							</Drawer>
						);
					}

					return (
						<Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
							<DialogContent className="sm:!max-w-[400px] flex flex-col gap-0 bg-[var(--background)] p-0 overflow-visible">
								<DialogHeader className="border-b px-4 py-3 flex-row items-center justify-between space-y-0">
									<DialogTitle>{reminderTitle}</DialogTitle>
									<DialogClose asChild>
										<Button variant="ghost" className="h-8 w-8 p-0"><X className="h-4 w-4" /></Button>
									</DialogClose>
								</DialogHeader>
								<div className="px-4 py-3">
									{ReminderFields(true)}
								</div>
								{ReminderFooter}
							</DialogContent>
						</Dialog>
					);
				})()}

				{/* Finish by (Due Date & Time) Modal - Dialog on desktop, Drawer on mobile */}
				{(() => {
					const hasRmdr = !!taskData?.rmdrDT;
					const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
					const isAssigneeMine = myUserIDs.includes(Number(taskData?.pocUserID)) || Number(taskData?.pocUserID) === currentUserID;
					const showReminderSection = isAssigneeMine;
					const dueTitle = dueModalType === 'RMDR'
						? (hasRmdr ? "Edit Reminder Time" : "Set Reminder Time")
						: dueModalType === 'BOTH'
							? "Edit Due & Reminder Time"
							: "Edit Finish by Time";
					const now = new Date();
					let isDuePast = false;
					let isDueToday = false;
					if (dueDraftDate && !isNaN(dueDraftDate.getTime())) {
						const todayMidnight = new Date(now);
						todayMidnight.setHours(0, 0, 0, 0);

						const dueMidnight = new Date(dueDraftDate);
						dueMidnight.setHours(0, 0, 0, 0);

						const combinedDue = dueDraftTime
							? new Date(combineDateTime(format(dueDraftDate, 'dd-MMM-yy'), dueDraftTime))
							: dueMidnight;

						if (dueMidnight < todayMidnight) {
							isDuePast = true;
						} else if (dueMidnight.getTime() === todayMidnight.getTime()) {
							isDueToday = true;
						}
					}

					const desktopLabel = (
						<div className="flex items-center justify-between mb-1">
							<Label className="text-sm font-medium text-gray-700">Finish by Date & Time</Label>
							{isDuePast && (
								<button
									type="button"
									onClick={handleSetDueToday}
									className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
								>
									Today
								</button>
							)}
							{isDueToday && (
								<button
									type="button"
									onClick={handleSetDueTmrw}
									className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
								>
									Tmrw
								</button>
							)}
						</div>
					);
					const currentDueDraftObj = dueDraftDate
						? new Date(combineDateTime(format(dueDraftDate, 'dd-MMM-yy'), dueDraftTime || '17:00'))
						: (taskData?.dueDT ? new Date(taskData.dueDT) : null);
					const isTaskOverdueForReminder = currentDueDraftObj && currentDueDraftObj < new Date();
					const dateMinMax = {
						minDate: (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })(),
						maxDate: (currentDueDraftObj && !isTaskOverdueForReminder) ? (() => { const d = new Date(currentDueDraftObj); d.setHours(23, 59, 59, 999); return d; })() : undefined,
					};

					const DueFields = (isDesktopView) => (
						<div className="space-y-4">
							<div>
								{desktopLabel}
								<div className="flex flex-nowrap items-center gap-3 mt-1.5">
									<div className="flex-1 min-w-0">
										<DatePicker
											value={dueDraftDate}
											onChange={handleDueDraftDateChange}
											placeholder="Select Date"
											minDate={new Date(new Date().setHours(0, 0, 0, 0))}
										/>
									</div>
									<div className="flex-1 min-w-0">
										<TimeInputWithDialog
											value={dueDraftTime}
											onChange={handleDueDraftTimeChange}
											placeholder="Select Time"
											fieldID="dueTimePicker"
											canClose={false}
											hideLabel={true}
										/>
									</div>
								</div>
							</div>

							{canEditDue && showReminderSection && (
								<div>
									{reminderDate ? (
										<>
											<div className="flex items-center justify-between mb-1">
												<Label className="text-sm font-medium text-gray-700">Reminder Date & Time</Label>
												<button
													type="button"
													onClick={() => {
														setReminderDate(null);
														setReminderTime(userPlanTime);
													}}
													className="text-sahi-red hover:opacity-70 transition-opacity"
													title="Remove Reminder"
												>
													<Trash2 className="h-3.5 w-3.5" />
												</button>
											</div>
											<div className="flex flex-nowrap items-center gap-3 mt-1.5">
												<div className="flex-1 min-w-0">
													<DatePicker
														value={reminderDate}
														onChange={handleReminderDateChange}
														placeholder="Select Date"
														minDate={dateMinMax.minDate}
														maxDate={(dueDraftDate && !isTaskOverdueForReminder) ? (() => { const d = new Date(dueDraftDate); d.setHours(23, 59, 59, 999); return d; })() : undefined}
													/>
												</div>
												<div className="flex-1 min-w-0">
													<TimeInputWithDialog
														value={reminderTime}
														onChange={handleReminderTimeChange}
														placeholder="Select Time"
														fieldID="rmdrTimePicker"
														canClose={false}
														hideLabel={true}
													/>
												</div>
											</div>
										</>
									) : (
										<div className="flex items-center justify-end mb-1">
											<button
												type="button"
												onClick={() => {
													presetReminderToDueDT();
												}}
												className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
											>
												+ Remind me
											</button>
										</div>
									)}
								</div>
							)}
						</div>
					);
					const DueFooter = (
						<div className="flex gap-3 px-4 pb-4 pt-4 border-t border-zinc-100 shrink-0">
							<Button onClick={handleSaveDue} disabled={savingDue} className="sahi-primary-button flex-1 sm:flex-none">
								<Check className="h-4 w-4" strokeWidth={3} /> {savingDue ? "Saving..." : "Save"}
							</Button>
							<Button variant="outline" onClick={() => setDueModalOpen(false)} disabled={savingDue} className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]">
								<X className="w-3 h-3" /> Close
							</Button>
						</div>
					);

					if (isMobile) {
						return (
							<Drawer open={dueModalOpen} onOpenChange={setDueModalOpen}>
								<DrawerContent
									className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
									style={{ maxHeight: `${dueVvHeight * 0.85}px`, height: 'auto' }}
								>
									<div className="flex flex-col" style={{ maxHeight: `${dueVvHeight * 0.85}px` }}>
										<div className="flex items-center justify-between px-4 h-16 shrink-0">
											<div className="flex gap-4 items-center">
												<button type="button" tabIndex={-1} onClick={() => setDueModalOpen(false)}
													className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500">
													<ArrowLeft className="h-[24px] w-[24px]" />
												</button>
												<p className="text-lg font-semibold text-gray-900">{dueTitle}</p>
											</div>
											{/* {isDuePast && (
												<button
													type="button"
													onClick={handleSetDueToday}
													className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
												>
													Today
												</button>
											)}
											{isDueToday && (
												<button
													type="button"
													onClick={handleSetDueTmrw}
													className="text-[14px] underline underline-offset-4 leading-none text-muted-foreground hover:underline cursor-pointer font-medium"
												>
													Tmrw
												</button>
											)} */}
										</div>
										<div className="overflow-y-auto custom-scroll flex-1 px-4 py-1">
											{DueFields(false)}
										</div>
										{DueFooter}
									</div>
								</DrawerContent>
							</Drawer>
						);
					}

					return (
						<Dialog open={dueModalOpen} onOpenChange={setDueModalOpen}>
							<DialogContent className="sm:!max-w-[400px] flex flex-col gap-0 bg-[var(--background)] p-0 overflow-visible">
								<DialogHeader className="border-b px-4 py-3 flex-row items-center justify-between space-y-0">
									<DialogTitle>{dueTitle}</DialogTitle>
									<DialogClose asChild>
										<Button variant="ghost" className="h-8 w-8 p-0"><X className="h-4 w-4" /></Button>
									</DialogClose>
								</DialogHeader>
								<div className="px-4 py-3">
									{DueFields(true)}
								</div>
								{DueFooter}
							</DialogContent>
						</Dialog>
					);
				})()}

				{/* Updates Timeline Modal/Drawer Container */}
				{updatesModalOpen && (() => {
					const isMeetingTask = Number(taskData?.class || taskData?.Class || taskData?.taskClass || 1) === 2;
					const UpdatesTitle = isMeetingTask ? "Meeting Notes" : "Task Updates";
					const UpdatesBody = (
						<div className="overflow-y-auto custom-scroll flex-1 p-4 pt-0">
							<UpdatesTimelineContent />
						</div>
					);

					if (isMobile) {
						return (
							<Drawer open={updatesModalOpen} onOpenChange={setUpdatesModalOpen}>
								<DrawerContent
									className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden"
									style={{ maxHeight: `${window.innerHeight * 0.85}px`, height: 'auto' }}
								>
									<div className="flex flex-col animate-in fade-in slide-in-from-bottom-5 duration-200" style={{ maxHeight: `${window.innerHeight * 0.85}px` }}>
										<div className="flex items-center justify-between px-4 h-16 shrink-0">
											<div className="flex gap-4 items-center">
												<button type="button" tabIndex={-1} onClick={() => setUpdatesModalOpen(false)}
													className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 cursor-pointer">
													<ArrowLeft className="h-[24px] w-[24px]" />
												</button>
												<p className="text-lg font-semibold text-gray-900">{UpdatesTitle}</p>
											</div>
											<span className="text-xs bg-sahi-green-light px-2 py-1 rounded-full font-medium text-sahi-green">
												{taskData?.updates?.length || 0} {isMeetingTask ? "Notes" : "Updates"}
											</span>
										</div>
										{UpdatesBody}
									</div>
								</DrawerContent>
							</Drawer>
						);
					}

					return (
						<Dialog open={updatesModalOpen} onOpenChange={setUpdatesModalOpen}>
							<DialogContent className="sm:!max-w-[400px] flex flex-col gap-0 bg-[var(--background)] p-0 overflow-hidden !max-h-[60vh]">
								<DialogHeader className="border-b px-4 py-3  mb-3 flex-row items-center justify-between space-y-0 shrink-0">
									<div className="flex items-center gap-2">
										<DialogTitle className="text-lg md:text-xl !pr-1 font-semibold">{UpdatesTitle}</DialogTitle>
										<span className="text-xs bg-sahi-green-light px-2 py-0.5 rounded-full font-medium text-sahi-green">
											{taskData?.updates?.length || 0} {isMeetingTask ? "Notes" : "Updates"}
										</span>
									</div>
									<DialogClose asChild>
										<Button variant="ghost" className="h-8 w-8 p-0 cursor-pointer"><X className="h-4 w-4" /></Button>
									</DialogClose>
								</DialogHeader>
								{UpdatesBody}
							</DialogContent>
						</Dialog>
					);
				})()}

			</Dialog>
		</TooltipProvider>
	);
};

export default TaskDetails;
